<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <div class="instances-server-view-switcher" role="group" aria-label="Tipo de vista">
                <b-button variant="link" class="list-view-icon-btn"
                          :class="{ 'list-view-icon-btn--active': serverViewMode == 'cards' }"
                          v-b-tooltip.hover.bottom title="Vista de tarjetas" aria-label="Vista de tarjetas"
                          @click="serverViewMode = 'cards'">
                  <AppIcon name="view_module" :fill="serverViewMode == 'cards'" />
                </b-button>
                <b-button variant="link" class="list-view-icon-btn"
                          :class="{ 'list-view-icon-btn--active': serverViewMode == 'tree' }"
                          v-b-tooltip.hover.bottom title="Vista en árbol" aria-label="Vista en árbol"
                          @click="serverViewMode = 'tree'">
                  <AppIcon name="account_tree" :fill="serverViewMode == 'tree'" />
                </b-button>
                <b-button variant="link" class="list-view-icon-btn"
                          :class="{ 'list-view-icon-btn--active': serverViewMode == 'full' }"
                          v-b-tooltip.hover.bottom title="Vista completa (todo desplegado)" aria-label="Vista completa (todo desplegado)"
                          @click="serverViewMode = 'full'">
                  <AppIcon name="view_list" :fill="serverViewMode == 'full'" />
                </b-button>
              </div>
              <b-button variant="link" class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn list-view-icon-btn--primary"
                        v-b-tooltip.hover.bottom title="Agregar servidor" aria-label="Agregar servidor" @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 240px; max-width: 340px;">
              <label class="filter-label">Buscar</label>
              <b-form-input type="text" size="sm" placeholder="Nombre o subdominio" v-model="filters.name" v-on:keyup.enter.native="filter" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 240px; max-width: 320px;">
              <label class="filter-label">Agrupación</label>
              <v-select :options="arr.select.categories" v-model="filters.categorie" placeholder="Todas" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 190px; max-width: 250px;">
              <label class="filter-label">Tipo</label>
              <v-select :options="arr.filter.type" v-model="filters.type" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 240px; max-width: 300px;">
              <label class="filter-label">Estado operativo</label>
              <v-select :options="arr.filter.health" v-model="filters.health" :clearable="false" :multiple="false" :select-on-tab="true" />
            </div>
            <b-button variant="outline-dark" @click="filter()" size="sm" v-b-tooltip.hover title="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">  
        <b-row>
          <b-col>
            <div v-if="table.isBusy" class="instances-server-list-loader" role="status" aria-live="polite">
              <b-spinner variant="primary" label="Cargando servidores"></b-spinner>
              <span>Cargando servidores...</span>
            </div>
            <div v-else-if="table.items.length" class="instances-server-groups">
              <b-card v-for="group in groupedServers" :key="group.id" no-body class="instances-server-group-card">
                <button type="button" class="instances-server-group-card__header" :class="{ 'instances-server-group-card__header--static': serverViewMode == 'full' }" @click="toggleServerGroup(group.id)">
                  <span class="instances-server-group-card__icon"><AppIcon name="folder" font-scale="1.2" /></span>
                  <div class="instances-server-group-card__summary">
                    <h3>{{group.name}}</h3>
                    <span>{{group.items.length}} {{group.items.length == 1 ? 'servidor' : 'servidores'}}</span>
                  </div>
                  <div class="instances-server-group-card__health" :aria-label="getGroupHealthLabel(group.health)">
                    <span v-if="group.health.off" class="instances-server-group-card__health-item instances-server-group-card__health-item--danger"
                          v-b-tooltip.hover :title="group.health.off + ' servidor(es) apagado(s)'">
                      <AppIcon name="power_settings_new" />
                      {{group.health.off}} {{group.health.off == 1 ? 'apagada' : 'apagadas'}}
                    </span>
                    <span v-if="group.health.withoutStatus" class="instances-server-group-card__health-item instances-server-group-card__health-item--warning"
                          v-b-tooltip.hover :title="group.health.withoutStatus + ' servidor(es) sin información de estado'">
                      <AppIcon name="help" />
                      {{group.health.withoutStatus}} {{group.health.withoutStatus == 1 ? 'sin estado' : 'sin estado'}}
                    </span>
                    <span v-if="group.health.resourcesCritical" class="instances-server-group-card__health-item instances-server-group-card__health-item--danger"
                          v-b-tooltip.hover :title="group.health.resourcesCritical + ' servidor(es) con al menos un recurso al 90% o más'">
                      <AppIcon name="warning" />
                      {{group.health.resourcesCritical}} {{group.health.resourcesCritical == 1 ? 'servidor crítico' : 'servidores críticos'}}
                    </span>
                    <span v-if="group.health.resourcesWarning" class="instances-server-group-card__health-item instances-server-group-card__health-item--warning"
                          v-b-tooltip.hover :title="group.health.resourcesWarning + ' servidor(es) con al menos un recurso superior a 80% y menor a 90%'">
                      <AppIcon name="warning" />
                      {{group.health.resourcesWarning}} {{group.health.resourcesWarning == 1 ? 'servidor con recursos altos' : 'servidores con recursos altos'}}
                    </span>
                    <span v-if="group.health.processing" class="instances-server-group-card__health-item instances-server-group-card__health-item--info"
                          v-b-tooltip.hover :title="group.health.processing + ' servidor(es) en instalación o actualización'">
                      <AppIcon name="progress_activity" class="instances-process-indicator__icon" /> {{group.health.processing}} procesando
                    </span>
                    <span v-if="!group.health.off && !group.health.withoutStatus && !group.health.resourcesCritical && !group.health.resourcesWarning && !group.health.processing"
                          class="instances-server-group-card__health-item instances-server-group-card__health-item--success">
                      <AppIcon name="check_circle" /> Saludable
                    </span>
                  </div>
                  <AppIcon v-if="serverViewMode != 'full'" class="instances-server-group-card__toggle" :name="isServerGroupOpen(group.id) ? 'expand_less' : 'expand_more'" />
                </button>

                <b-collapse :visible="isServerGroupOpen(group.id)" class="instances-server-group-card__body">
                  <div v-if="serverViewMode == 'cards'" class="instances-server-group-card__cards">
                  <article v-for="server in group.items" :key="server.id" class="instances-server-card">
                    <div class="instances-server-card__heading">
                      <div>
                        <div class="instances-server-card__title">
                          <span class="instances-server-card__id">#{{server.id}}</span>
                          <h4>{{server.name}}</h4>
                          <span v-if="isServerProcessing(server)" class="instances-process-indicator"><AppIcon name="progress_activity" class="instances-process-indicator__icon" /> Procesando</span>
                          <AppIcon v-if="server.show_laravel" name="layers" class="text-info" label="Disponible en Laravel" />
                          <AppIcon v-if="server.show_docker" name="deployed_code" class="text-secondary" label="Disponible en Docker" />
                        </div>
                        <div class="instances-server-card__meta">
                          <b-badge variant="dark" v-if="server.type=='fullcontrol'">Zyncro</b-badge>
                          <b-badge variant="info" v-if="server.type=='readonly'">Laravel</b-badge>
                          <b-badge variant="secondary" v-if="server.type=='docker'">Docker</b-badge>
                          <b-badge variant="warning" v-if="server.type=='backups'">Backups</b-badge>
                          <b-badge variant="secondary" v-if="server.type=='customer'">Solo SSH</b-badge>
                          <b-link v-if="server.subdomain" target="_blank" :href="'http://'+server.subdomain"><AppIcon name="language" /> {{server.subdomain}}</b-link>
                        </div>
                      </div>

                      <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle">
                        <template #button-content><AppIcon name="more_vert" label="Opciones" /></template>
                        <b-dropdown-item @click="actionServer(server)" v-if="server.type != 'customer'"><AppIcon name="terminal" /> Comandos</b-dropdown-item>
                        <b-dropdown-item @click="getAccessSSH(server)"><AppIcon name="key" /> SSH</b-dropdown-item>
                        <b-dropdown-item @click="logServer(server)" v-if="server.type != 'customer'"><AppIcon name="article" /> Log</b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="edit(server)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                        <b-dropdown-item @click="remove(server)"><AppIcon name="delete" class="text-danger" /> Eliminar</b-dropdown-item>
                      </b-dropdown>
                    </div>

                    <div v-if="getParseNotification(server) || server.observations" class="instances-server-card__alerts">
                      <div v-if="getParseNotification(server)" class="instances-server-card__alert instances-server-card__alert--notification">
                        <AppIcon name="notifications" />
                        <div><b>Notificaciones</b><span>{{getParseNotification(server)}}</span></div>
                      </div>
                      <div v-if="server.observations" class="instances-server-card__alert instances-server-card__alert--observation">
                        <AppIcon name="chat" />
                        <div><b>Observaciones</b><span>{{server.observations}}</span></div>
                      </div>
                    </div>

                    <div class="instances-server-card__metrics">
                      <section class="instances-server-card__section">
                        <span class="instances-server-card__section-label">Estado</span>
                        <b-badge :id="'server-status-card-' + server.id" :variant="getStatusVariant(server)" class="instances-server-status">
                          <AppIcon :name="getStatusIcon(server)" /> {{getStatusLabel(server)}}
                        </b-badge>
                        <b-popover :target="'server-status-card-' + server.id" triggers="hover" placement="top">
                          <template #title><AppIcon name="dns" class="mr-1" /> Estado del servidor</template>
                          <b>Servidor:</b> {{server.name}}<br><b>Estado:</b> {{getServerDetails(server, 'status').status}}<br>
                          <span v-if="getServerDetails(server, 'status').error"><b>Error:</b> {{getServerDetails(server, 'status').error}}<br></span>
                          <span v-else-if="getServerDetails(server, 'status').version"><b>Versión:</b> {{getServerDetails(server, 'status').version}}<br><b>Kernel:</b> {{getServerDetails(server, 'status').kernel}}<br></span>
                          <span v-if="getServerDetails(server, 'status').datetime"><b>Última sincronización:</b> {{moment(getServerDetails(server, 'status').datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                        </b-popover>
                      </section>

                      <section class="instances-server-card__section">
                        <span class="instances-server-card__section-label">Recursos</span>
                        <div v-if="getServerResources(server).length" class="instances-server-resources">
                          <span v-for="resource in getServerResources(server)" :key="resource.key" :id="'server-card-' + resource.key + '-' + server.id" class="instances-server-resource" :class="getResourceTone(server, resource.key)">
                            <AppIcon :name="resource.icon" font-scale="1.65" weight="500" /><span>{{formatResourceUsage(server, resource.key)}}</span><small>{{resource.label}}</small>
                            <b-popover :target="'server-card-' + resource.key + '-' + server.id" triggers="hover" placement="top">
                              <template #title><AppIcon :name="resource.icon" class="mr-1" /> {{resource.title}}</template>
                              <b>Servidor:</b> {{server.name}}<br>
                              <span v-if="resource.key == 'cpu'"><b>Cantidad:</b> {{getServerDetails(server, resource.key).total}}<br><b>Usado:</b> {{getServerDetails(server, resource.key).usage}}<br></span>
                              <span v-if="resource.key == 'memory'"><b>Total:</b> {{getServerDetails(server, resource.key).total}}M<br><b>Usado:</b> {{getServerDetails(server, resource.key).usage}}M - {{getServerDetails(server, resource.key).usage_percent}}<br><b>Libre:</b> {{getServerDetails(server, resource.key).free}}M<br><b>Disponible:</b> {{getServerDetails(server, resource.key).avalible}}M<br></span>
                              <span v-if="resource.key == 'disk'"><b>Total:</b> {{getServerDetails(server, resource.key).total}}<br><b>Libre:</b> {{getServerDetails(server, resource.key).free}}<br><b>Usado:</b> {{getServerDetails(server, resource.key).usage}} - {{getServerDetails(server, resource.key).usage_percent}}<br></span>
                              <span v-if="getServerDetails(server, resource.key).datetime"><b>Última sincronización:</b> {{moment(getServerDetails(server, resource.key).datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                            </b-popover>
                          </span>
                        </div>
                        <span v-else class="text-muted">Sin datos</span>
                      </section>

                      <section class="instances-server-card__section instances-server-card__section--services">
                        <span class="instances-server-card__section-label">Servicios</span>
                        <div v-if="getServerServices(server).length" class="instances-server-services">
                          <span v-for="service in getServerServices(server)" :key="service.key" :id="'server-card-' + service.key + '-' + server.id" class="instances-server-service" :class="getServiceTone(server, service.key)">
                            <AppIcon :name="service.icon" font-scale="1.65" weight="500" /><span>{{service.label}}</span><small>{{getServerDetails(server, service.key).status}}</small>
                            <b-popover :target="'server-card-' + service.key + '-' + server.id" triggers="hover" placement="top">
                              <template #title><AppIcon :name="service.icon" class="mr-1" /> {{service.label}}</template>
                              <b>Servidor:</b> {{server.name}}<br><b>Estado:</b> {{getServerDetails(server, service.key).status}}<br>
                              <span v-if="getServerDetails(server, service.key).version"><b>Versión:</b> {{getServerDetails(server, service.key).version}}<br></span>
                              <span v-if="service.key == 'php' && getServerDetails(server, service.key).PHP_FPM72"><b>PHP-FPM 7.2:</b> {{getServerDetails(server, service.key).PHP_FPM72}}<br><b>PHP-FPM 7.4:</b> {{getServerDetails(server, service.key).PHP_FPM74}}<br></span>
                              <span v-if="getServerDetails(server, service.key).datetime"><b>Última sincronización:</b> {{moment(getServerDetails(server, service.key).datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                            </b-popover>
                          </span>
                        </div>
                        <span v-else class="text-muted">No aplica</span>
                      </section>
                    </div>
                  </article>
                  </div>
                  <div v-else class="instances-server-tree-view">
                    <article v-for="server in group.items" :key="server.id" class="instances-server-tree-view__server">
                      <span class="instances-server-tree-view__branch"><AppIcon name="dns" /></span>
                      <div class="instances-server-tree-view__server-header">
                        <div class="instances-server-tree-view__identity">
                          <div class="instances-server-card__title">
                            <span class="instances-server-card__id">#{{server.id}}</span>
                            <h4>{{server.name}}</h4>
                            <span v-if="isServerProcessing(server)" class="instances-process-indicator"><AppIcon name="progress_activity" class="instances-process-indicator__icon" /> Procesando</span>
                            <b-link v-if="server.subdomain" :href="'http://'+server.subdomain" target="_blank" class="instances-server-tree-view__link"><AppIcon name="language" /> {{server.subdomain}}</b-link>
                            <span :id="'server-tree-info-' + server.id" tabindex="0" class="instances-server-tree-view__more"><AppIcon name="info" label="Ver más" /></span>
                            <b-popover :target="'server-tree-info-' + server.id" triggers="hover focus click blur" placement="top" custom-class="instances-tree-info-popover">
                              <template #title><AppIcon name="info" class="mr-1" /> {{server.name}}</template>
                              <b>Tipo:</b> {{server.type}}<br>
                              <span v-if="server.show_laravel"><b>Disponible en Laravel</b><br></span>
                              <span v-if="server.show_docker"><b>Disponible en Docker</b><br></span>
                              <span v-if="getParseNotification(server)"><b>Notificaciones:</b> {{getParseNotification(server)}}<br></span>
                              <span v-if="server.observations"><b>Observaciones:</b> {{server.observations}}</span>
                            </b-popover>
                          </div>
                        </div>
                        <b-badge :id="'server-tree-status-' + server.id" :variant="getStatusVariant(server)" class="instances-server-status instances-server-tree-view__status">
                          <AppIcon :name="getStatusIcon(server)" /> {{getStatusLabel(server)}}
                        </b-badge>
                        <b-popover :target="'server-tree-status-' + server.id" triggers="hover" placement="top">
                          <template #title><AppIcon name="dns" class="mr-1" /> Estado del servidor</template>
                          <b>Servidor:</b> {{server.name}}<br><b>Estado:</b> {{getServerDetails(server, 'status').status}}<br>
                          <span v-if="getServerDetails(server, 'status').error"><b>Error:</b> {{getServerDetails(server, 'status').error}}<br></span>
                          <span v-else-if="getServerDetails(server, 'status').version"><b>Versión:</b> {{getServerDetails(server, 'status').version}}<br><b>Kernel:</b> {{getServerDetails(server, 'status').kernel}}<br></span>
                          <span v-if="getServerDetails(server, 'status').datetime"><b>Última sincronización:</b> {{moment(getServerDetails(server, 'status').datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                        </b-popover>
                        <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle">
                          <template #button-content><AppIcon name="more_vert" label="Opciones" /></template>
                          <b-dropdown-item @click="actionServer(server)" v-if="server.type != 'customer'"><AppIcon name="terminal" /> Comandos</b-dropdown-item>
                          <b-dropdown-item @click="getAccessSSH(server)"><AppIcon name="key" /> SSH</b-dropdown-item>
                          <b-dropdown-item @click="logServer(server)" v-if="server.type != 'customer'"><AppIcon name="article" /> Log</b-dropdown-item>
                          <b-dropdown-divider />
                          <b-dropdown-item @click="edit(server)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                          <b-dropdown-item @click="remove(server)"><AppIcon name="delete" class="text-danger" /> Eliminar</b-dropdown-item>
                        </b-dropdown>
                      </div>
                      <div class="instances-server-tree-view__details">
                        <span v-for="resource in getServerResources(server)" :key="resource.key" :id="'server-tree-' + resource.key + '-' + server.id" class="instances-server-tree-view__item" :class="getResourceTone(server, resource.key)">
                          <AppIcon :name="resource.icon" /><b>{{resource.label}}</b><span>{{formatResourceUsage(server, resource.key)}}</span>
                          <b-popover :target="'server-tree-' + resource.key + '-' + server.id" triggers="hover" placement="top">
                            <template #title><AppIcon :name="resource.icon" class="mr-1" /> {{resource.title}}</template>
                            <b>Servidor:</b> {{server.name}}<br>
                            <span v-if="resource.key == 'cpu'"><b>Cantidad:</b> {{getServerDetails(server, resource.key).total}}<br><b>Usado:</b> {{getServerDetails(server, resource.key).usage}}<br></span>
                            <span v-if="resource.key == 'memory'"><b>Total:</b> {{getServerDetails(server, resource.key).total}}M<br><b>Usado:</b> {{getServerDetails(server, resource.key).usage}}M - {{getServerDetails(server, resource.key).usage_percent}}<br><b>Libre:</b> {{getServerDetails(server, resource.key).free}}M<br><b>Disponible:</b> {{getServerDetails(server, resource.key).avalible}}M<br></span>
                            <span v-if="resource.key == 'disk'"><b>Total:</b> {{getServerDetails(server, resource.key).total}}<br><b>Libre:</b> {{getServerDetails(server, resource.key).free}}<br><b>Usado:</b> {{getServerDetails(server, resource.key).usage}} - {{getServerDetails(server, resource.key).usage_percent}}<br></span>
                            <span v-if="getServerDetails(server, resource.key).datetime"><b>Última sincronización:</b> {{moment(getServerDetails(server, resource.key).datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                          </b-popover>
                        </span>
                        <span v-for="service in getServerServices(server)" :key="service.key" :id="'server-tree-' + service.key + '-' + server.id" class="instances-server-tree-view__item" :class="getServiceTone(server, service.key)">
                          <AppIcon :name="service.icon" /><b>{{service.label}}</b><span>{{getServerDetails(server, service.key).status}}</span>
                          <b-popover :target="'server-tree-' + service.key + '-' + server.id" triggers="hover" placement="top">
                            <template #title><AppIcon :name="service.icon" class="mr-1" /> {{service.label}}</template>
                            <b>Servidor:</b> {{server.name}}<br><b>Estado:</b> {{getServerDetails(server, service.key).status}}<br>
                            <span v-if="getServerDetails(server, service.key).version"><b>Versión:</b> {{getServerDetails(server, service.key).version}}<br></span>
                            <span v-if="service.key == 'php' && getServerDetails(server, service.key).PHP_FPM72"><b>PHP-FPM 7.2:</b> {{getServerDetails(server, service.key).PHP_FPM72}}<br><b>PHP-FPM 7.4:</b> {{getServerDetails(server, service.key).PHP_FPM74}}<br></span>
                            <span v-if="getServerDetails(server, service.key).datetime"><b>Última sincronización:</b> {{moment(getServerDetails(server, service.key).datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                          </b-popover>
                        </span>
                        <span v-if="!getServerResources(server).length && !getServerServices(server).length" class="instances-server-tree-view__empty">Sin datos de monitoreo</span>
                      </div>
                    </article>
                  </div>
                </b-collapse>
              </b-card>
            </div>

            <b-table v-if="false" class="app-table"
                    ref="table"
                    responsive
                    :stacked="isMobileViewport ? 'md' : false"
                    sticky-header="60vh"
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="table.items"
                    :fields="table.fields"                    
                    :current-page="table.currentPage"
                    :per-page="table.perPage"
                    :busy="table.isBusy">

                <template v-slot:cell(id)="data">
                  <b># {{data.item.id}}</b>
                </template>

                <template v-slot:cell(name)="data">
                  <div class="instances-server-name">
                    <div class="instances-server-tree__category">
                      <AppIcon name="folder" />
                      {{ data.item.categorie ? data.item.categorie.name : 'Sin categoría' }}
                    </div>
                    <div class="instances-server-tree__node">
                      <div class="instances-server-name__title">
                        <b>{{data.item.name}}</b>
                        <span v-if="data.item.show_laravel" v-b-tooltip.hover title="Disponible en Laravel">
                          <AppIcon name="layers" class="text-info" />
                        </span>
                        <span v-if="data.item.show_docker" v-b-tooltip.hover title="Disponible en Docker">
                          <AppIcon name="deployed_code" class="text-secondary" />
                        </span>
                      </div>
                    </div>
                    <div class="instances-server-name__meta">
                      <b-badge variant="dark" v-if="data.item.type=='fullcontrol'">Zyncro</b-badge>
                      <b-badge variant="info" v-if="data.item.type=='readonly'">Laravel</b-badge>
                      <b-badge variant="secondary" v-if="data.item.type=='docker'">Docker</b-badge>
                      <b-badge variant="warning" v-if="data.item.type=='backups'">Backups</b-badge>
                      <b-badge variant="secondary" v-if="data.item.type=='customer'">Solo SSH</b-badge>
                      <span v-if="getParseNotification(data.item)" class="instances-server-alert text-warning" v-b-tooltip.hover :title="getParseNotification(data.item)">
                        <AppIcon name="notifications" label="Notificaciones configuradas" />
                      </span>
                      <span v-if="data.item.observations" class="instances-server-alert text-info" v-b-tooltip.hover :title="data.item.observations">
                        <AppIcon name="chat" label="Tiene observaciones" />
                      </span>
                    </div>
                    <b-link v-if="data.item.subdomain" class="instances-server-name__link" target="_blank" :href="'http://'+data.item.subdomain">
                      <AppIcon name="language" /> {{data.item.subdomain}}
                    </b-link>
                  </div>
                </template>

                <template v-slot:cell(notifications)="data">                  
                  <div v-if="getParseNotification(data.item)" v-b-tooltip.hover :title="getParseNotification(data.item)">
                    <AppIcon name="notifications" />
                  </div>                           
                </template>

                <template v-slot:cell(observations)="data">
                  <div v-if="data.item.observations" v-b-tooltip.hover :title="data.item.observations">
                    <AppIcon name="chat" />
                  </div>                           
                </template>

                <template v-slot:cell(status)="data">
                  <b-badge :id="'server-status-' + data.item.id" :variant="getStatusVariant(data.item)" class="instances-server-status">
                    <AppIcon :name="getStatusIcon(data.item)" />
                    {{ getStatusLabel(data.item) }}
                  </b-badge>
                  <b-popover :target="'server-status-' + data.item.id" triggers="hover" placement="top">
                    <template #title><AppIcon name="dns" class="mr-1" /> Estado del servidor</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{getServerDetails(data.item, 'status').status}}<br>
                    <div v-if="getServerDetails(data.item, 'status').error">
                      <b>Error:</b> {{getServerDetails(data.item, 'status').error}}<br>
                    </div>
                    <div v-else>
                      <span v-if="getServerDetails(data.item, 'status').version"><b>Versión:</b> {{getServerDetails(data.item, 'status').version}}<br></span>
                      <span v-if="getServerDetails(data.item, 'status').kernel"><b>Kernel:</b> {{getServerDetails(data.item, 'status').kernel}}<br></span>
                    </div>
                    <span v-if="getServerDetails(data.item, 'status').datetime"><b>Última sincronización:</b> {{moment(getServerDetails(data.item, 'status').datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                  </b-popover>
                </template>

                <template v-slot:cell(resources)="data">
                  <div v-if="getServerResources(data.item).length" class="instances-server-resources">
                    <span v-for="resource in getServerResources(data.item)"
                          :key="resource.key"
                          :id="'server-' + resource.key + '-' + data.item.id"
                          class="instances-server-resource"
                          :class="getResourceTone(data.item, resource.key)">
                      <AppIcon :name="resource.icon" />
                      <span>{{ formatResourceUsage(data.item, resource.key) }}</span>
                      <small>{{ resource.label }}</small>
                      <b-popover :target="'server-' + resource.key + '-' + data.item.id" triggers="hover" placement="top">
                        <template #title><AppIcon :name="resource.icon" class="mr-1" /> {{ resource.title }}</template>
                        <b>Servidor:</b> {{data.item.name}}<br>
                        <span v-if="resource.key == 'cpu'"><b>Cantidad:</b> {{getServerDetails(data.item, resource.key).total}}<br><b>Usado:</b> {{getServerDetails(data.item, resource.key).usage}}<br></span>
                        <span v-if="resource.key == 'memory'"><b>Total:</b> {{getServerDetails(data.item, resource.key).total}}M<br><b>Usado:</b> {{getServerDetails(data.item, resource.key).usage}}M - {{getServerDetails(data.item, resource.key).usage_percent}}<br><b>Libre:</b> {{getServerDetails(data.item, resource.key).free}}M<br><b>Disponible:</b> {{getServerDetails(data.item, resource.key).avalible}}M<br></span>
                        <span v-if="resource.key == 'disk'"><b>Total:</b> {{getServerDetails(data.item, resource.key).total}}<br><b>Libre:</b> {{getServerDetails(data.item, resource.key).free}}<br><b>Usado:</b> {{getServerDetails(data.item, resource.key).usage}} - {{getServerDetails(data.item, resource.key).usage_percent}}<br></span>
                        <span v-if="getServerDetails(data.item, resource.key).datetime"><b>Última sincronización:</b> {{ moment(getServerDetails(data.item, resource.key).datetime).format('DD/MM/YYYY HH:mm:ss') }}</span>
                      </b-popover>
                    </span>
                  </div>
                  <span v-else class="text-muted">Sin datos</span>
                </template>

                <template v-slot:cell(services)="data">
                  <div v-if="getServerServices(data.item).length" class="instances-server-services">
                    <span v-for="service in getServerServices(data.item)"
                          :key="service.key"
                          :id="'server-' + service.key + '-' + data.item.id"
                          class="instances-server-service"
                          :class="getServiceTone(data.item, service.key)">
                      <AppIcon :name="service.icon" :label="service.label" />
                      <span>{{service.label}}</span>
                      <small>{{getServerDetails(data.item, service.key).status}}</small>
                      <b-popover :target="'server-' + service.key + '-' + data.item.id" triggers="hover" placement="top">
                        <template #title><AppIcon :name="service.icon" class="mr-1" /> {{ service.label }}</template>
                        <b>Servidor:</b> {{data.item.name}}<br>
                        <b>Estado:</b> {{getServerDetails(data.item, service.key).status}}<br>
                        <span v-if="getServerDetails(data.item, service.key).version"><b>Versión:</b> {{getServerDetails(data.item, service.key).version}}<br></span>
                        <span v-if="service.key == 'php' && getServerDetails(data.item, service.key).PHP_FPM72"><b>PHP-FPM 7.2:</b> {{getServerDetails(data.item, service.key).PHP_FPM72}}<br><b>PHP-FPM 7.4:</b> {{getServerDetails(data.item, service.key).PHP_FPM74}}<br></span>
                        <span v-if="getServerDetails(data.item, service.key).datetime"><b>Última sincronización:</b> {{moment(getServerDetails(data.item, service.key).datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                      </b-popover>
                    </span>
                  </div>
                  <span v-else class="text-muted">No aplica</span>
                </template>
                
                <template v-slot:cell(cpu)="data">
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.cpu">
                    <div v-if="showFieldServer(data.item, 'cpu') <= 0.8" class="text-success font-weight-bold" :id="'server-cpu-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'cpu')) }} Usado
                    </div>
                    <div v-if="showFieldServer(data.item, 'cpu') > 0.8 && showFieldServer(data.item, 'cpu') < 0.9" class="text-warning font-weight-bold" :id="'server-cpu-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'cpu')) }} Usado
                    </div>                  
                    <div v-if="showFieldServer(data.item, 'cpu') >= 0.9" class="text-danger font-weight-bold" :id="'server-cpu-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'cpu')) }} Usado
                    </div>
                  </div>

                  <b-popover :target="'server-cpu-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.cpu">

                    <template #title>Estado CPU</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Cantidad:</b> {{JSON.parse(data.item.cpu).total}}<br>
                    <b>Usado:</b> {{JSON.parse(data.item.cpu).usage}}<br>                    
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.cpu).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                  
                </template>

                <template v-slot:cell(disk)="data">
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.disk">
                    <div v-if="showFieldServer(data.item, 'disk') <= 0.8" class="text-success font-weight-bold" :id="'server-disk-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'disk')) }} Usado
                    </div>
                    <div v-if="showFieldServer(data.item, 'disk') > 0.8 && showFieldServer(data.item, 'disk') < 0.9" class="text-warning font-weight-bold" :id="'server-disk-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'disk')) }} Usado
                    </div>                  
                    <div v-if="showFieldServer(data.item, 'disk') >= 0.9" class="text-danger font-weight-bold" :id="'server-disk-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'disk')) }} Usado
                    </div>
                  </div>

                  <b-popover :target="'server-disk-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.disk">

                    <template #title>Estado del Disco</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Total:</b> {{JSON.parse(data.item.disk).total}}<br>
                    <b>Libre:</b> {{JSON.parse(data.item.disk).free}}<br>
                    <b>Usado:</b> {{JSON.parse(data.item.disk).usage}} - {{JSON.parse(data.item.disk).usage_percent}}<br>                    
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.disk).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                  
                </template>

                <template v-slot:cell(memory)="data">
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.memory">
                    <div v-if="showFieldServer(data.item, 'memory') <= 0.8" class="text-success font-weight-bold" :id="'server-memory-' + data.item.id">                      
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'memory')) }} Usado
                    </div>
                    <div v-if="showFieldServer(data.item, 'memory') > 0.8 && showFieldServer(data.item, 'memory') < 0.9" class="text-warning font-weight-bold" :id="'server-memory-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'memory')) }} Usado
                    </div>                  
                    <div v-if="showFieldServer(data.item, 'memory') >= 0.9" class="text-danger font-weight-bold" :id="'server-memory-' + data.item.id">
                      {{ Intl.NumberFormat('es-AR',{style:'percent', maximumFractionDigits: 2 }).format( showFieldServer(data.item, 'memory')) }} Usado
                    </div>                  
                  </div>

                  <b-popover :target="'server-memory-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.memory">

                    <template #title>Estado de la Memoria</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Total:</b> {{JSON.parse(data.item.memory).total}}M<br>                    
                    <b>Usado:</b> {{JSON.parse(data.item.memory).usage}}M - {{JSON.parse(data.item.memory).usage_percent}}<br>
                    <b>Libre:</b> {{JSON.parse(data.item.memory).free}}M<br>
                    <b>Disponible:</b> {{JSON.parse(data.item.memory).avalible}}M<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.memory).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>  
                </template>

                <template v-slot:cell(git)="data">         
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.git && !data.item.status_install && data.item.type!='backups'">         
                    <div v-if="showFieldServer(data.item, 'git') == 'Activo'" class="text-success font-weight-bold" :id="'server-git-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'git') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-git-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>                  
                  </div>

                  <b-popover :target="'server-git-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.git && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado de GIT</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.git).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.git).version}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.git).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                  
                </template>

                <template v-slot:cell(apache)="data">         
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.apache && !data.item.status_install && data.item.type!='backups'">         
                    <div v-if="showFieldServer(data.item, 'apache') == 'Activo'" class="text-success font-weight-bold" :id="'server-apache-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'apache') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-apache-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>                  
                  </div>

                  <b-popover :target="'server-apache-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.apache && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado del Apache</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.apache).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.apache).version}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.apache).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                  
                </template>

                <template v-slot:cell(composer)="data">         
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.composer && !data.item.status_install && data.item.type!='backups'">         
                    <div v-if="showFieldServer(data.item, 'composer') == 'Activo'" class="text-success font-weight-bold" :id="'server-composer-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'composer') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-composer-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>                  
                  </div>

                  <b-popover :target="'server-composer-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.composer && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado de Composer</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.composer).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.composer).version}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.composer).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                  
                </template>

                <template v-slot:cell(php)="data">         
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.php && !data.item.status_install && data.item.type!='backups'">         
                    <div v-if="showFieldServer(data.item, 'php') == 'Activo'" class="text-success font-weight-bold" :id="'server-php-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'php') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-php-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>                  
                  </div>

                  <b-popover :target="'server-php-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.php && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado de PHP</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.php).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.php).version}}<br>
                    <hr>
                    <b>PHP-FPM 7.2:</b> <span :class="{'font-weight-bold': true, 'text-success': JSON.parse(data.item.php).PHP_FPM72 == 'Activo', 'text-danger': JSON.parse(data.item.php).PHP_FPM72 != 'Activo'}">{{JSON.parse(data.item.php).PHP_FPM72}}</span><br>
                    <b>PHP-FPM 7.4:</b> <span :class="{'font-weight-bold': true, 'text-success': JSON.parse(data.item.php).PHP_FPM74 == 'Activo', 'text-danger': JSON.parse(data.item.php).PHP_FPM74 != 'Activo'}">{{JSON.parse(data.item.php).PHP_FPM74}}</span><br>
                    <hr>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.php).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                  
                </template>

                <template v-slot:cell(mysql)="data">
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.mysql && !data.item.status_install && data.item.type!='backups'">
                    <div v-if="showFieldServer(data.item, 'mysql') == 'Activo'" class="text-success font-weight-bold" :id="'server-mysql-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'mysql') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-mysql-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>   
                  </div>

                  <b-popover :target="'server-mysql-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.mysql && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado del MySql</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.mysql).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.mysql).version}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.mysql).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                    
                </template>

                <template v-slot:cell(supervisor)="data">
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.supervisor && !data.item.status_install && data.item.type!='backups'">
                    <div v-if="showFieldServer(data.item, 'supervisor') == 'Activo'" class="text-success font-weight-bold" :id="'server-supervisor-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'supervisor') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-supervisor-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>   
                  </div>

                  <b-popover :target="'server-supervisor-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.supervisor && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado del Supervisor</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.supervisor).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.supervisor).version}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.supervisor).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                   
                </template>

                <template v-slot:cell(certbot)="data">         
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.certbot && !data.item.status_install && data.item.type!='backups'">         
                    <div v-if="showFieldServer(data.item, 'certbot') == 'Activo'" class="text-success font-weight-bold" :id="'server-certbot-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'certbot') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-certbot-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>                  
                  </div>

                  <b-popover :target="'server-certbot-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.certbot && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado de CERTBOT</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.certbot).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.certbot).version}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.certbot).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                  
                </template>                

                <template v-slot:cell(docker)="data">    
                  <div v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.docker && !data.item.status_install && data.item.type!='backups'">         
                    <div v-if="showFieldServer(data.item, 'docker') == 'Activo'" class="text-success font-weight-bold" :id="'server-docker-' + data.item.id">
                      <AppIcon name="circle" fill class="text-success" />
                    </div>
                    <div v-if="showFieldServer(data.item, 'docker') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-docker-' + data.item.id">
                      <AppIcon name="circle" fill class="text-danger" />
                    </div>                  
                  </div>

                  <b-popover :target="'server-docker-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="showFieldServer(data.item, 'status') && showFieldServer(data.item, 'status') == 'Iniciado' && data.item.docker && !data.item.status_install && data.item.type!='backups'">

                    <template #title>Estado de DOCKER</template>
                    <b>Servidor:</b> {{data.item.name}}<br>
                    <b>Estado:</b> {{JSON.parse(data.item.docker).status}}<br>
                    <b>Versión:</b> {{JSON.parse(data.item.docker).version}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.docker).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>            
                </template>       

                <template v-slot:cell(f_action)="data">                  
                  <b-dropdown right no-caret size="sm" variant="link" boundary="window"
                              :toggle-class="isMobileViewport ? 'crud-instances-server-action-toggle-labeled' : 'app-table-action-toggle'"
                              :class="{ 'pull-right': !isMobileViewport }">
                    <template #button-content>
                      <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                      <span v-if="isMobileViewport">Opciones</span>
                    </template>
                    <b-dropdown-item @click="actionServer(data.item)" v-if="data.item.type != 'customer'">
                      <AppIcon name="terminal" /> Comandos
                    </b-dropdown-item>         
                    <b-dropdown-item @click="getAccessSSH(data.item)">
                      <AppIcon name="key" /> SSH
                    </b-dropdown-item>                        
                    <b-dropdown-item @click="logServer(data.item)" v-if="data.item.type != 'customer'">
                      <AppIcon name="article" /> Log
                    </b-dropdown-item>                                  

                    <b-dropdown-header>Acciones</b-dropdown-header>
                    <b-dropdown-item @click="edit(data.item)">
                      <AppIcon name="edit" /> Editar
                    </b-dropdown-item>
                    <b-dropdown-item @click="remove(data.item)">
                      <AppIcon name="delete" class="text-danger" /> Eliminar
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
            </b-table>
            <EmptyState v-if="!table.items.length && !table.isBusy" icon="dns" title="No se encontraron servidores" />
          </b-col>
        </b-row>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <div>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <AppIcon name="arrow_back" class="mr-1" />
              Volver
            </b-button>                        
          </div>

        </div>
      </b-card>

      <div id="command_terminal"></div>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- CRUD MAIN -->
      <b-modal v-model="modal.form.active"
              size="xl"
              :key="modal.form._id"
              :id="modal.form._id">                  
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="dns" /></span>
          <h5 class="app-modal-header__title">{{ modal.form.title }}</h5>
        </div>
        <b-row>
          <b-col md="6">
            <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
              <b-tab>
                <template #title><AppIcon name="description" class="mr-1" /> General</template>
                <b-row>      
                  <b-col md="8">
                    <b-form-group label="Nombre">
                      <b-form-input type="text"
                                    size="sm"
                                    v-model="crud.form.name"
                                    required
                                    placeholder="Ingresar un nombre"
                                    autocomplete="off">
                      </b-form-input>
                    </b-form-group>
                  </b-col>   
                    <b-col md="4">            
                      <b-form-group label="Tipo">                    
                        <b-form-select v-model="crud.form.type" :options="arr.select.type"></b-form-select>                    
                      </b-form-group>
                    </b-col>                  
                  <b-col md="12">
                    <FindObject render="search"
                                type="instances-categories"
                                @select-object="loadInstancesCategories($event)"
                                :valueID="crud.form.categories_id"
                                :where="filterCategoriesServer" />           
                  </b-col>                         
                  <b-col md="12" v-if="crud.form.type=='fullcontrol'">
                    <b-form-group label="Sub Dominio">
                      <b-form-input type="text"
                                    size="sm"
                                    v-model="crud.form.subdomain"
                                    required
                                    placeholder="Ingresar un subdominio"
                                    autocomplete="off"
                                    readonly 
                                    onfocus="this.removeAttribute('readonly');">
                      </b-form-input>
                    </b-form-group>
                  </b-col>   
                  
                  <b-col md="12" v-if="crud.form.type=='fullcontrol'">
                    <b-form-checkbox v-model="crud.form.show_laravel" 
                                      switch 
                                      size="sm" 
                                      class="pull-left">
                      Permite instalar instancias "LARAVEL"
                    </b-form-checkbox> 
                  </b-col>  
                  <b-col md="12" v-if="crud.form.type=='fullcontrol' || crud.form.type=='readonly'">
                    <b-form-checkbox v-model="crud.form.show_docker" 
                                      switch 
                                      size="sm" 
                                      class="pull-left">
                      Permite instalar instancias "DOCKER"
                    </b-form-checkbox> 
                  </b-col>  
                  
                  <b-col md="12" class="mt-4">
                    <b-form-group label="Observaciones">
                      <app-textarea                
                        v-model="crud.form.observations"
                        lazy
                        placeholder="Ingresar las observaciones..."
                        rows="3"
                        max-rows="6"
                      ></app-textarea>
                    </b-form-group>
                  </b-col>                                       
                </b-row>
              </b-tab>
              <b-tab>
                <template #title><AppIcon name="key" class="mr-1" /> SSH</template>
                <b-row>          
                  <b-col md="8">
                    <b-form-group label="IP Host">
                      <b-form-input type="text"
                                    size="sm"
                                    v-model="crud.form.host"                            
                                    placeholder="xxx.xxx.xxx.xxx"
                                    autocomplete="off">
                      </b-form-input>
                    </b-form-group>
                  </b-col>           
                  <b-col md="4">
                    <b-form-group label="Puerto">
                      <b-form-input type="number"
                                    size="sm"
                                    v-model="crud.form.port"                            
                                    placeholder="xxxx"
                                    autocomplete="off">
                      </b-form-input>
                    </b-form-group>
                  </b-col>         
                  <b-col md="6">
                    <b-form-group label="Usuario SSH">
                      <b-form-input type="text"
                                    size="sm"
                                    autocomplete="off"
                                    v-model="crud.form.user"                            
                                    name="ssh_user"                          
                                    placeholder="Ingresar Usuario Servidor">
                      </b-form-input>
                    </b-form-group>
                  </b-col>           
                  <b-col md="6">            
                    <b-form-group label="Contraseña SSH">
                      <b-input-group class="w-100">    
                        <b-form-input :type="password.typePasswordServer"
                                      size="sm"
                                      autocomplete="off"
                                      v-model="crud.form.password"  
                                      name="ssh_password"                          
                                      placeholder="Ingresar Contraseña Servidor">
                        </b-form-input>
                        <b-input-group-append>
                          <b-button variant="secondary" size="sm" @click="changePasswordServer()">
                            <AppIcon :name="password.iconPasswordServer" />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>              
                    </b-form-group>
                  </b-col>                                                                  
                </b-row>
              </b-tab>
              <b-tab v-if="crud.form.type!='docker' && crud.form.type!='backups' && crud.form.type != 'customer'">
                <template #title><AppIcon name="database" class="mr-1" /> Base de Datos</template>
                <b-row>
                  <b-col md="6">
                    <b-form-group label="Usuario DB">
                      <b-form-input type="text"
                                    size="sm"
                                    v-model="crud.form.database_user"                            
                                    placeholder="Ingresar Usuario Base de Datos"
                                    autocomplete="off">
                      </b-form-input>
                    </b-form-group>
                  </b-col>           
                  <b-col md="6">            
                    <b-form-group label="Contraseña DB">
                      <b-input-group class="w-100">    
                        <b-form-input :type="password.typePasswordDatabase"
                                      size="sm"
                                      v-model="crud.form.database_password"                            
                                      placeholder="Ingresar Contraseña Base de Datos"
                                      autocomplete="off">
                        </b-form-input>
                        <b-input-group-append>
                          <b-button variant="secondary" size="sm" @click="changePasswordDatabase()">
                            <AppIcon :name="password.iconPasswordDatabase" />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>              
                    </b-form-group>
                  </b-col>                   
                </b-row>
              </b-tab>
              <b-tab v-if="crud.form.type!='docker' && crud.form.type!='backups' && crud.form.type != 'customer'">
                <template #title><AppIcon name="notifications" class="mr-1" /> Notificaciones</template>
                    
                <Notifications @get-notifications="getNotificationsData($event)" 
                               :role_notification="crud.form.roles_notification"
                               :staff_notification="crud.form.staff_notification"
                               :email_notification="crud.form.email_notification" />
            
              </b-tab>
            </b-tabs>
          </b-col>
          <b-col md="6">
            <b-card bg-variant="light" header="Implementación" text-variant="dark">
              <b-card-text>
                <p>
                  Podrá <b>conectarse y monitorear</b> el <b>rendimiento</b> de un servidor <b>via SSH</b> solo necesita las <b>credenciales de acceso.</b>
                </p>            
                
                <hr>                
                
                <p>
                  Podrá <b>conectarse, instalar y/o monitoriar</b> un servidor (VPS) <b>via SSH</b>, el mismo debe cumplir con los siguientes <b>requisitos</b>:
                </p>
                <ul>
                  <li>Instalación básica de <b>Ubuntu Server (versión mínima 20.04 LTS)</b></li>
                  <li>En caso de necesitar <b>acceso remoto</b> a la base de datos <b>MySQL</b>, abrir el puerto <b>3306</b> en el <b>Firewall</b></li>
                  <li>Aputar un <b>subdominio</b> a la <b>IP del servidor</b> (Tipo A => [IP] => *.s1.dominio.com)</li>
                </ul>              
              </b-card-text>
            </b-card>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" /> Guardar</b-button>
        </div>
      </b-modal>

      <!-- CRUD COMMAND -->
      <b-modal v-model="modal.command.active"
              size="lg"
              :key="modal.command._id"
              :id="modal.command._id">                  
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="terminal" /></span>
          <h5 class="app-modal-header__title">{{ modal.command.title }}</h5>
        </div>

        <b-row v-if="selectedServer">
          <b-col md="3" class="instances-server-command-panel-column">
            <aside class="instances-server-command-panel" aria-label="Comandos del servidor">
              <h6><AppIcon name="terminal" /> Comandos</h6>

              <section class="instances-server-command-group">
                <b-button v-b-toggle.server-command-monitoring variant="link" class="instances-server-command-group__toggle">
                  <span><AppIcon name="monitoring" /> Monitoreo</span>
                  <AppIcon name="expand_more" />
                </b-button>
                <b-collapse id="server-command-monitoring" visible accordion="server-commands">
                  <div class="instances-server-command-group__actions">
                    <span class="instances-server-command-subsection">CPU</span>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra la cantidad de núcleos de CPU del servidor." class="instances-server-command-action" @click="getActionServer('cpu-total', 'Cantidad de CPU')"><AppIcon name="memory" /> CPU: cantidad</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el porcentaje de uso actual de CPU del servidor." class="instances-server-command-action" @click="getActionServer('cpu-usage', 'Uso de CPU (%)')"><AppIcon name="memory" /> CPU: uso</b-button>
                    <span class="instances-server-command-subsection">Disco</span>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el tamaño total del disco del servidor, en GB." class="instances-server-command-action" @click="getActionServer('disk-total', 'Tamaño total del disco (G)')"><AppIcon name="storage" /> Disco: tamaño</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el espacio de disco usado, en GB." class="instances-server-command-action" @click="getActionServer('disk-usage-g', 'Uso del disco (G)')"><AppIcon name="storage" /> Disco: usado</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el porcentaje de uso del disco." class="instances-server-command-action" @click="getActionServer('disk-usage-percent', 'Uso del disco (%)')"><AppIcon name="storage" /> Disco: uso</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el espacio de disco disponible, en GB." class="instances-server-command-action" @click="getActionServer('disk-avalible', 'Tamaño disponible en disco (G)')"><AppIcon name="storage" /> Disco: disponible</b-button>
                    <span class="instances-server-command-subsection">Memoria</span>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra la memoria RAM total del servidor, en MB." class="instances-server-command-action" @click="getActionServer('memory-total', 'Tamaño total de memoria (M)')"><AppIcon name="memory" /> RAM: tamaño</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra la memoria RAM usada actualmente, en MB." class="instances-server-command-action" @click="getActionServer('memory-usage', 'Uso de memoria (M)')"><AppIcon name="memory" /> RAM: usada</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el porcentaje de uso de memoria RAM." class="instances-server-command-action" @click="getActionServer('memory-usage-percent', 'Uso de memoria (%)')"><AppIcon name="memory" /> RAM: uso</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra la memoria RAM libre, en MB." class="instances-server-command-action" @click="getActionServer('memory-free', 'Memoria libre (M)')"><AppIcon name="memory" /> RAM: libre</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra la memoria RAM disponible (libre + cache liberable), en MB." class="instances-server-command-action" @click="getActionServer('memory-avalible', 'Memoria disponible (M)')"><AppIcon name="memory" /> RAM: disponible</b-button>
                  </div>
                </b-collapse>
              </section>

              <section v-if="showServer || selectedServer.type != 'backups'" class="instances-server-command-group">
                <b-button v-b-toggle.server-command-services variant="link" class="instances-server-command-group__toggle">
                  <span><AppIcon name="settings" /> Servicios</span>
                  <AppIcon name="expand_more" />
                </b-button>
                <b-collapse id="server-command-services" accordion="server-commands">
                  <div class="instances-server-command-group__actions">
                    <span v-if="showServer" class="instances-server-command-subsection">Git</span>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Muestra si Git está instalado y su versión en el servidor." class="instances-server-command-action" @click="getActionServer('git-status', 'Estado del servicio [GIT]')"><AppIcon name="account_tree" /> Git: estado</b-button>
                    <b-button v-if="showServer && showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Instala Git en el servidor." class="instances-server-command-action" @click="getActionServer('git-install', 'Instalar [GIT]')"><AppIcon name="account_tree" /> Git: instalar</b-button>
                    <span v-if="showServer" class="instances-server-command-subsection">Apache</span>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Muestra el estado del servicio Apache (activo/inactivo)." class="instances-server-command-action" @click="getActionServer('apache-status', 'Estado del servicio [APACHE]')"><AppIcon name="language" /> Apache: estado</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Inicia el servicio Apache." class="instances-server-command-action" @click="getActionServer('apache-start', 'Iniciar [APACHE]')"><AppIcon name="play_arrow" /> Apache: iniciar</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Detiene el servicio Apache." class="instances-server-command-action" @click="getActionServer('apache-stop', 'Parar [APACHE]')"><AppIcon name="stop" /> Apache: detener</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Reinicia el servicio Apache." class="instances-server-command-action" @click="getActionServer('apache-restart', 'Reiniciar [APACHE]')"><AppIcon name="restart_alt" /> Apache: reiniciar</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Recarga la configuración de Apache sin cortar las conexiones activas." class="instances-server-command-action" @click="getActionServer('apache-reload', 'Recargar [APACHE]')"><AppIcon name="refresh" /> Apache: recargar</b-button>
                    <b-button v-if="showServer && showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Instala Apache en el servidor." class="instances-server-command-action" @click="getActionServer('apache-install', 'Instalar [APACHE]')"><AppIcon name="language" /> Apache: instalar</b-button>
                    <span v-if="showServer" class="instances-server-command-subsection">Composer</span>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Muestra si Composer está instalado y su versión." class="instances-server-command-action" @click="getActionServer('composer-status', 'Estado del servicio [COMPOSER]')"><AppIcon name="extension" /> Composer: estado</b-button>
                    <b-button v-if="showServer && showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Instala Composer en el servidor." class="instances-server-command-action" @click="getActionServer('composer-install', 'Instalar [COMPOSER]')"><AppIcon name="extension" /> Composer: instalar</b-button>
                    <span v-if="showServer" class="instances-server-command-subsection">PHP</span>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Muestra si PHP está instalado y su versión." class="instances-server-command-action" @click="getActionServer('php-status', 'Estado del servicio [PHP]')"><AppIcon name="code" /> PHP: estado</b-button>
                    <b-button v-if="showServer && showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Instala PHP en el servidor." class="instances-server-command-action" @click="getActionServer('php-install', 'Instalar [PHP]')"><AppIcon name="code" /> PHP: instalar</b-button>
                    <template v-if="selectedServer.type != 'backups'">
                      <span class="instances-server-command-subsection">MySQL</span>
                      <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el estado del servicio MySQL (activo/inactivo)." class="instances-server-command-action" @click="getActionServer('mysql-status', 'Estado del servicio [MYSQL]')"><AppIcon name="database" /> MySQL: estado</b-button>
                      <b-button variant="link" v-b-tooltip.hover.righttop.window title="Inicia el servicio MySQL." class="instances-server-command-action" @click="getActionServer('mysql-start', 'Iniciar [MYSQL]')"><AppIcon name="play_arrow" /> MySQL: iniciar</b-button>
                      <b-button variant="link" v-b-tooltip.hover.righttop.window title="Detiene el servicio MySQL." class="instances-server-command-action" @click="getActionServer('mysql-stop', 'Parar [MYSQL]')"><AppIcon name="stop" /> MySQL: detener</b-button>
                      <b-button variant="link" v-b-tooltip.hover.righttop.window title="Reinicia el servicio MySQL." class="instances-server-command-action" @click="getActionServer('mysql-restart', 'Reiniciar [MYSQL]')"><AppIcon name="restart_alt" /> MySQL: reiniciar</b-button>
                      <b-button v-if="showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Instala MySQL en el servidor." class="instances-server-command-action" @click="getActionServer('mysql-install', 'Instalar [MYSQL]')"><AppIcon name="database" /> MySQL: instalar</b-button>
                    </template>
                    <span v-if="showServer" class="instances-server-command-subsection">Supervisor</span>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Muestra el estado del servicio Supervisor (activo/inactivo)." class="instances-server-command-action" @click="getActionServer('supervisor-status', 'Estado del servicio [SUPERVISOR]')"><AppIcon name="settings" /> Supervisor: estado</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Inicia el servicio Supervisor." class="instances-server-command-action" @click="getActionServer('supervisor-start', 'Iniciar [SUPERVISOR]')"><AppIcon name="play_arrow" /> Supervisor: iniciar</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Detiene el servicio Supervisor." class="instances-server-command-action" @click="getActionServer('supervisor-stop', 'Parar [SUPERVISOR]')"><AppIcon name="stop" /> Supervisor: detener</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Reinicia el servicio Supervisor." class="instances-server-command-action" @click="getActionServer('supervisor-restart', 'Reiniciar [SUPERVISOR]')"><AppIcon name="restart_alt" /> Supervisor: reiniciar</b-button>
                    <b-button v-if="showServer && showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Instala Supervisor en el servidor." class="instances-server-command-action" @click="getActionServer('supervisor-install', 'Instalar [SUPERVISOR]')"><AppIcon name="settings" /> Supervisor: instalar</b-button>
                    <span v-if="showServer" class="instances-server-command-subsection">Certbot</span>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Muestra si Certbot está instalado y su versión." class="instances-server-command-action" @click="getActionServer('certbot-status', 'Estado del servicio [CERTBOT]')"><AppIcon name="verified_user" /> Certbot: estado</b-button>
                    <b-button v-if="showServer && showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Instala Certbot en el servidor, usado para generar certificados SSL." class="instances-server-command-action" @click="getActionServer('certbot-install', 'Instalar [CERTBOT]')"><AppIcon name="verified_user" /> Certbot: instalar</b-button>
                    <span v-if="showServer" class="instances-server-command-subsection">Docker</span>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Muestra el estado del servicio Docker (activo/inactivo)." class="instances-server-command-action" @click="getActionServer('docker-status', 'Estado del servicio [DOCKER]')"><AppIcon name="deployed_code" /> Docker: estado</b-button>
                    <b-button v-if="showServer" variant="link" v-b-tooltip.hover.righttop.window title="Instala Docker en el servidor." class="instances-server-command-action" @click="getActionServer('docker-install', 'Instalar [DOCKER]')"><AppIcon name="deployed_code" /> Docker: instalar</b-button>
                  </div>
                </b-collapse>
              </section>

              <section class="instances-server-command-group">
                <b-button v-b-toggle.server-command-server variant="link" class="instances-server-command-group__toggle">
                  <span><AppIcon name="dns" /> Servidor</span>
                  <AppIcon name="expand_more" />
                </b-button>
                <b-collapse id="server-command-server" accordion="server-commands">
                  <div class="instances-server-command-group__actions">
                    <span class="instances-server-command-subsection">Consulta</span>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra el estado general de conectividad y recursos del servidor." class="instances-server-command-action" @click="getActionServer('server-status', 'Estado del servidor')"><AppIcon name="dns" /> Estado</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Corre un chequeo completo de todos los servicios y recursos del servidor." class="instances-server-command-action" @click="getActionServer('server-fullcheck', 'Chequeo general')"><AppIcon name="fact_check" /> Chequeo general</b-button>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Muestra las tareas programadas (crontab) configuradas en el servidor." class="instances-server-command-action" @click="getActionServer('server-crontab', 'Tareas programadas (Crontab)')"><AppIcon name="schedule" /> Tareas programadas</b-button>
                    <span class="instances-server-command-subsection">Administración</span>
                    <b-button variant="link" v-b-tooltip.hover.righttop.window title="Reinicia el servidor completo. Corta la conexión durante el reinicio." class="instances-server-command-action instances-server-command-action--danger" @click="getActionServer('server-restart', 'Reiniciar servidor')"><AppIcon name="restart_alt" /> Reiniciar servidor</b-button>
                    <b-button v-if="showFullControl" variant="link" v-b-tooltip.hover.righttop.window title="Corre la instalación inicial completa del servidor (todos los servicios)." class="instances-server-command-action" @click="getActionServer('server-install', 'Instalar servidor')"><AppIcon name="download" /> Instalar servidor</b-button>
                  </div>
                </b-collapse>
              </section>
            </aside>
          </b-col>
          <b-col md="9" class="app-command-terminal-column">
            <section class="app-command-terminal">
              <div class="app-command-terminal__header">
                <h4>{{this.modal.command.subtitle}}</h4>
                <div class="app-command-terminal__actions">
                <span v-if="selectedServer.status_install">
                  <span class="app-command-terminal__process"><AppIcon name="progress_activity" /> Instalando</span>
                </span>                  
                <b-button v-if="!SSHLoader && SSHConsole" variant="outline-secondary" size="sm" class="app-command-terminal__copy" @click="copySSHConsole()">
                  <AppIcon name="content_copy" class="mr-1" /> Copiar
                </b-button>
                <b-button variant="danger" size="sm" class="app-command-terminal__refresh" title="Refrescar" @click="refreshServer()" v-if="!SSHLoader">
                  <AppIcon name="refresh" label="Refrescar" />
                </b-button>
                </div>
              </div>
              <div v-if="!SSHLoader" ref="sshConsole" class="ssh-console-server" v-html="SSHConsole"></div>
              <div v-else class="app-command-terminal__loader">
              <b-spinner label="Spinning" type="grow"></b-spinner>
              </div>
            </section>
          </b-col>
        </b-row>

        <div slot="modal-footer" class="ssh-console-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="closeActionServer()"><AppIcon name="x" class="mr-1" /> Cerrar</b-button>
        </div>
      </b-modal>     

      <!-- CRUD LOG -->
      <b-modal v-model="modal.log.active"
              size="xl">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="article" /></span>
          <h5 class="app-modal-header__title">{{ modal.log.title }}</h5>
        </div>

        <b-row>
          <b-col md="12">
            <div v-if="tableLog.isBusy" class="instances-server-log-loader" role="status" aria-live="polite">
              <b-spinner variant="primary" label="Cargando registros del log"></b-spinner>
              <span>Cargando registros del log...</span>
            </div>
            <b-table v-else-if="tableLog.items.length" class="app-table app-table--compact"
                    responsive
                    :stacked="isMobileViewport ? 'md' : false"
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="tableLog.items"
                    :fields="tableLog.fields"                    
                    :current-page="tableLog.currentPage"
                    :per-page="tableLog.perPage">

                <template v-slot:cell(date)="data">
                  {{moment(data.item.date).format('DD/MM/YYYY HH:mm:ss')}}
                </template>
                
                <template v-slot:cell(reference)="data">
                  <b>{{data.item.reference}}</b>
                </template>

            </b-table>
            <EmptyState v-else icon="article" title="No se encontraron registros" />
          </b-col>
          <b-col v-if="!tableLog.isBusy && tableLog.items.length" md="12" class="mt-3">
            <nav>
              <b-pagination class="pull-right mb-0"
                            size="sm"
                            pills=""
                            :total-rows="getRowCount(tableLog.items)"
                            :per-page="tableLog.perPage"
                            v-model="tableLog.currentPage" />
            </nav>
          </b-col>             
        </b-row>

        <div slot="modal-footer" class="ssh-console-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="closeLogServer()">Cerrar</b-button>                  
        </div>
      </b-modal>          
    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper' 
  import FindObject from '@/components/inc/find/findObject'  
  import Storage from '@/handler/storageLocal'
  import Notifications from '@/components/inc/notifications'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      FindObject,    
      Notifications,  
      TabHeaderActions,
    },        
    data: () => {
      return {      
        access: {
          module_id: Modules.INSTANCIAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudInstancesServer',
          elements: {}
        },         
        table : {
          items: [],
          fields: [],
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,                
        },
        filtersOpen: false,
        serverViewMode: 'tree',
        isMobileViewport: false,
        mobileMql: null,
        expandedServerGroups: {},
        tableLog: {
          items: [],
          fields: [
            {key: 'date', label: 'Fecha-Hora', class: 'align-middle'},          
            {key: 'reference', label: 'Referencia', class: 'align-middle'},          
            {key: 'title', label: 'Acción', class: 'align-middle'},          
            {key: 'description', label: 'Descripción', class: 'align-middle'},          
          ],
          currentPage: 1,
          perPage: 20,   
          isBusy: false,       
        },
        crud: {
          form: {
            id: 0,
            name: '',  
            host: '',
            port: '',
            user: '',
            password: '',
            database_user: '',
            database_password: '',
            categories_id: 0,
            categories: null,
            subdomain: '',
            observations: '',
            type: 'readonly',
            show_laravel: false,
            show_docker: false,
            roles_notification: [],            
            staff_notification: [],            
            email_notification: [],            
          },          
        },
        modal: {
          form: {
            active: false,
            title: ''
          },
          command: {
            active: false,
            title: '',
            subtitle: 'Terminal',
          }, 
          log: {
            active: false,
            title: '',            
          },                    
        },
        filters: {
          forceStatusServer: false,
          name: '', 
          categorie: null,
          type: '',
          health: { code: 'all', label: 'Todos' },
        },
        filterType: 'server',
        arr: {    
          roles: [],
          staff: [],      
          select: {
            categories: [],
            type: [
              { value: 'fullcontrol', text: 'Zyncro' },
              { value: 'readonly', text: 'Laravel' },              
              { value: 'docker', text: 'Docker' },
              { value: 'backups', text: 'Backups' },
              { value: 'customer', text: 'Solo SSH' },
            ], 
          },
          filter: {
            health: [
              { code: 'all', label: 'Todos' },
              { code: 'with_status', label: 'Con estado confirmado' },
              { code: 'without_status', label: 'Solo sin estado' },
              { code: 'problems', label: 'Con problemas operativos' },
              { code: 'attention', label: 'Sin estado o con problemas' },
            ],
            type: [
              { code: 'fullcontrol', label: 'Zyncro' },
              { code: 'readonly', label: 'Laravel' },              
              { code: 'docker', label: 'Docker' },
              { code: 'backups', label: 'Backups' },
              { code: 'customer', label: 'Solo SSH' },
            ],                        
          }
        },
        password: {
          typePasswordServer: 'password',
          iconPasswordServer: 'visibility',
          typePasswordDatabase: 'password',
          iconPasswordDatabase: 'visibility',          
        },  
        selectedServer: null,           
        SSHConsole: '',
        SSHLoader: false,
        server: {
          restart: false,
          searchInterval: null,         
        }       
      }
    },
    created () {     
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },  
    mounted() {      
      this.filterLoadCategories()
      this.restoreFilterStorage() 
      this.filtersOpen = Storage.getValue('filters_open_crud_instances_server') == 'true'
      var storedServerViewMode = Storage.getValue('view_mode_crud_instances_server')
      this.serverViewMode = ['cards', 'full'].indexOf(storedServerViewMode) >= 0 ? storedServerViewMode : 'tree'
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
      this.setFields()

      this.getStaff()
      this.getRoles()
      
      this.filter()      
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
      clearInterval(this.server.interval)
    },
    watch: {        
      filtersOpen(value) {
        Storage.setValue('filters_open_crud_instances_server', value ? 'true' : 'false')
      },
      serverViewMode(value) {
        Storage.setValue('view_mode_crud_instances_server', value)
      },
      isMobileViewport() {
        this.setFields()
      },
      'server.restart': function (newQuestion) {
        if(newQuestion) {
          this.server.interval = setInterval(() => {   
            this.filters.forceStatusServer = true
            this.filter()
          },10000)   
        } else {
          this.filters.forceStatusServer = false
          clearInterval(this.server.interval)     
        }
      }
    },    
    computed: {
      groupedServers() {
        var groups = {}
        this.table.items.forEach(item => {
          var category = item.categorie || { id: 'uncategorized', name: 'Sin categoría' }
          var key = category.id || 'uncategorized'
          if(!groups[key]) {
            groups[key] = { id: key, name: category.name, items: [] }
          }
          groups[key].items.push(item)
        })
        return Object.values(groups)
          .map(group => {
            group.health = this.getGroupHealth(group.items)
            return group
          })
          .sort((first, second) => this.getGroupHealthSeverity(second.health) - this.getGroupHealthSeverity(first.health)
            || this.getGroupHealthPriority(second.health) - this.getGroupHealthPriority(first.health)
            || this.getGroupCriticalHealthCount(second.health) - this.getGroupCriticalHealthCount(first.health)
            || first.name.localeCompare(second.name))
      },
      filterCategoriesServer() {
        return [{field: 'type', condition: this.filterType}]
      },
      showFullControl() {
        return this.selectedServer.type=='fullcontrol'
      },
      showServer() {
        return this.selectedServer.type=='readonly' || this.selectedServer.type=='fullcontrol' || this.selectedServer.type=='docker'
      },
      showBackups() {
        return this.selectedServer.type=='backups'
      },
      showSSH() {
        return this.selectedServer.type=='customer'
      }
    }, 
    methods: {
      setFields() {
        var center = this.alignClass('text-center')
        this.table.fields = [
          {key: 'id', label: 'ID', sortable: true, class: 'align-middle'},
          {key: 'name', label: 'Servidor', class: 'align-middle'},
          {key: 'status', label: 'Estado', class: 'align-middle ' + center},
          {key: 'resources', label: 'Recursos', class: 'align-middle ' + center},
          {key: 'services', label: 'Servicios', class: 'align-middle ' + center},
          {key: 'f_action', label: this.isMobileViewport ? 'Acciones' : '', class: 'align-middle ' + center},
        ]
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      // render
      getRowCount (items) {
        return items.length
      },

      // crud
      filter() {
        this.saveFilterStorage()

        this.table.isBusy = true

        var result = serviceAPI.filtrarServidor(this.filters, this.table.currentPage)

        result.then((response) => {
          var data = response.data
          this.table.tablePaginate = data

          this.table.items = data.data.filter(item => this.matchesSelectedHealth(item))
          if(this.filters.health && this.filters.health.code != 'all') {
            this.table.tablePaginate.total = this.table.items.length
            this.table.tablePaginate.last_page = 1
          }
          this.syncServerGroupsExpansion(this.table.items)

          if(this.$refs.table) {                        
            this.$refs.table.$forceUpdate()
          }      

          var obj = null
          var status = false
          data.data.forEach(element => {
            obj = JSON.parse(element.status)

            if(obj.status == 'Reiniciando') {
              status = true
            }
          });

          if(status) {
            this.server.restart = true
          } else {
            this.server.restart = false
            this.filters.forceStatusServer = false
            clearInterval(this.server.interval)               
          }

          this.table.isBusy = false          
        })
        .catch(() => {
          this.table.isBusy = false
          //this.$awn.alert(Error.showError(error))
        });        
      },
      add() {
        this.crud.form.id = 0
        this.crud.form.name = '' 
        this.crud.form.host = '' 
        this.crud.form.port = '' 
        this.crud.form.user = '' 
        this.crud.form.password = '' 
        this.crud.form.database_user = '' 
        this.crud.form.database_password = '' 
        this.crud.form.categories_id = 0
        this.crud.form.categories = null
        this.crud.form.subdomain  = ''
        this.crud.form.observations = ''
        this.crud.form.type = 'readonly'
        this.crud.form.show_laravel = false
        this.crud.form.show_docker = false
        this.crud.form.roles_notification = []        
        this.crud.form.staff_notification = []        
        this.crud.form.email_notification = []        

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Servidor"
        this.modal.form.active = true
      },
      edit(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name     
        this.crud.form.host = item.host
        this.crud.form.port = item.port
        this.crud.form.user = item.user
        this.crud.form.password = item.password
        this.crud.form.database_user = item.database_user
        this.crud.form.database_password = item.database_password
        this.crud.form.categories_id = item.categories_id
        this.crud.form.categories = item.categories
        this.crud.form.subdomain  = item.subdomain
        this.crud.form.observations = item.observations
        this.crud.form.type = item.type
        this.crud.form.show_laravel = item.show_laravel
        this.crud.form.show_docker = item.show_docker

        if(item.roles_notification) {
          this.crud.form.roles_notification = JSON.parse(item.roles_notification)
        } else {
          this.crud.form.roles_notification = item.roles_notification
        }

        if(item.staff_notification) {
          this.crud.form.staff_notification = JSON.parse(item.staff_notification)
        } else {
          this.crud.form.staff_notification = item.staff_notification
        }

        if(item.email_notification) {
          this.crud.form.email_notification = JSON.parse(item.email_notification)
        } else {
          this.crud.form.email_notification = item.email_notification
        }

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Editar Servidor"
        this.modal.form.active = true
      },
      remove(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name        

        this.confirmDialog('¿Desea borrar el servidor (' + this.crud.form.id + ') - '+ this.crud.form.name + '?', {
          title: 'Borrar Servidor',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.eliminarServidor(this.crud.form.id);

            result.then(() => {
              this.modal.form.active = false        
              loader.hide()
              this.filter()
              this.$awn.success("Servidor eliminado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      save() {
        let loader = this.$loading.show();

        var status = true        
        if(this.crud.form.email_notification && this.crud.form.email_notification.length) {
          let arrEmail = this.crud.form.email_notification
          arrEmail.forEach(element => {
            if(element) {
              var expreg = new RegExp("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$")
              if (!expreg.test(element)) {                          
                status = false
              }       
            }            
          });
        }
        if(!status) {
          this.$awn.alert("Unos de los emails posee un formato inválido")          
          loader.hide()
          return false
        }

        var result = this.crud.form.id
          ? serviceAPI.editarServidor(this.crud.form)
          : serviceAPI.agregarServidor(this.crud.form)

        result.then(() => {
          this.modal.form.active = false
          loader.hide()
          this.filter()
          this.$awn.success("Cambios guardado con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },

      // data notifications
      getNotificationsData(data) {        
        this.crud.form.email_notification = data.email_notification

        var role = []
        if(data.role_notification) {
          data.role_notification.forEach(element => {
            role.push(element.code)
          });
        }
        this.crud.form.roles_notification = role

        var staff = []
        if(data.staff_notification) {
          data.staff_notification.forEach(element => {
            staff.push(element.code)
          });
        }
        this.crud.form.staff_notification = staff              
      },
      getStaff() {
        serviceAPI.obtenerStaff().then(response => {
          var data = response.data    

          data.forEach(element => {              
            this.arr.staff.push(element)
          });             
        })
      },  
      getRoles() {
        serviceAPI.obtenerRoles().then(response => {
          var data = response.data    

          data.forEach(element => {              
            this.arr.roles.push(element)
          });                
        })
      },        
      getNotificationsName(type, id) {
        var name = ''
        if(type=='roles') {
          this.arr.roles.forEach(element => {            
            if(element.id == id) {
              name = element.name
            }
          });
        }
        if(type=='staff') {
          this.arr.staff.forEach(element => {            
            if(element.id == id) {
              name = element.name
            }
          });
        }        
        return name
      },
      getParseNotification(item) {  
        var resultRoles = ''
        var roles = item.roles_notification
        if(roles) {          
          resultRoles = 'ROLES: '
          JSON.parse(roles).forEach(element => {
            resultRoles = resultRoles + this.getNotificationsName('roles',element) + ", "
          });          
        }
        if(resultRoles) {
          resultRoles = resultRoles.substring(0,resultRoles.length - 2) + ' | '
        } 

        var resultStaff = ''
        var staff = item.staff_notification        
        if(staff) {        
          resultStaff = 'STAFF: '  
          JSON.parse(staff).forEach(element => {
            resultStaff = resultStaff + this.getNotificationsName('staff',element) + ", "
          });          
        }
        if(resultStaff) {
          resultStaff = resultStaff.substring(0,resultStaff.length - 2) + ' | '
        } 

        var resultEmails = ''
        var emails = item.email_notification        
        if(emails) {
          if(emails != '[]') {        
            resultEmails = 'EMAILS: '  
            JSON.parse(emails).forEach(element => {
              resultEmails = resultEmails + element + ", "
            });          
          }        
          if(resultEmails) {
            resultEmails = resultEmails.substring(0,resultEmails.length - 2) + ' | '
          } 
        }

        var result = resultRoles + resultStaff + resultEmails
        if(result) {
          result = result.substring(0,result.length - 3)
        }
        
        return result
      },       

      getServerDetails(item, key) {
        try {
          return item[key] ? JSON.parse(item[key]) : {}
        } catch (error) {
          return {}
        }
      },
      getStatusLabel(item) {
        return item.status_install ? 'Instalando' : (this.showFieldServer(item, 'status') || 'Sin estado')
      },
      getStatusVariant(item) {
        if(item.status_install) return 'info'
        if(this.showFieldServer(item, 'status') == 'Iniciado') return 'success'
        if(this.showFieldServer(item, 'status') == 'Reiniciando') return 'warning'
        return 'danger'
      },
      getStatusIcon(item) {
        if(item.status_install) return 'hourglass_top'
        if(this.showFieldServer(item, 'status') == 'Iniciado') return 'power'
        if(this.showFieldServer(item, 'status') == 'Reiniciando') return 'sync'
        return 'power_settings_new'
      },
      isServerGroupOpen(id) {
        if(this.serverViewMode == 'full') return true
        return !!this.expandedServerGroups[id]
      },
      toggleServerGroup(id) {
        if(this.serverViewMode == 'full') return
        this.$set(this.expandedServerGroups, id, !this.isServerGroupOpen(id))
      },
      syncServerGroupsExpansion(items) {
        var groupIds = {}
        items.forEach(item => {
          var category = item.categorie || { id: 'uncategorized' }
          groupIds[category.id || 'uncategorized'] = true
        })
        var groupKeys = Object.keys(groupIds)
        var hasPreviousState = Object.keys(this.expandedServerGroups).length > 0
        var expandedGroups = {}
        groupKeys.forEach(key => {
          expandedGroups[key] = hasPreviousState ? !!this.expandedServerGroups[key] : groupKeys.length == 1
        })
        this.expandedServerGroups = expandedGroups
      },
      isServerOff(item) {
        var status = this.getServerDetails(item, 'status').status
        return status == 'Apagado' || status == 'Inactivo'
      },
      isServerWithoutStatus(item) {
        return !item.status_install && !this.getServerDetails(item, 'status').status
      },
      getServerResourceAlertLevel(item) {
        var usages = this.getServerResources(item)
          .map(resource => this.showFieldServer(item, resource.key))
          .filter(usage => usage !== null)

        if(usages.some(usage => usage >= 0.9)) return 'critical'
        if(usages.some(usage => usage > 0.8)) return 'warning'
        return null
      },
      matchesSelectedHealth(item) {
        var health = this.filters.health ? this.filters.health.code : 'all'
        var withoutStatus = this.isServerWithoutStatus(item)
        var hasProblems = this.isServerOff(item) || !!this.getServerResourceAlertLevel(item)

        if(health == 'with_status') return !withoutStatus
        if(health == 'without_status') return withoutStatus
        if(health == 'problems') return hasProblems
        if(health == 'attention') return withoutStatus || hasProblems

        return true
      },
      getGroupHealth(items) {
        var health = { off: 0, withoutStatus: 0, resourcesCritical: 0, resourcesWarning: 0, processing: 0 }

        items.forEach(item => {
          if(this.isServerOff(item)) health.off++
          if(this.isServerWithoutStatus(item)) health.withoutStatus++
          if(this.isServerProcessing(item)) health.processing++

          var resourceAlert = this.getServerResourceAlertLevel(item)
          if(resourceAlert == 'critical') health.resourcesCritical++
          if(resourceAlert == 'warning') health.resourcesWarning++
        })

        return health
      },
      getGroupHealthPriority(health) {
        return health.off + health.withoutStatus + health.resourcesCritical + health.resourcesWarning + health.processing
      },
      getGroupHealthSeverity(health) {
        if(health.resourcesCritical) return 4
        if(health.off) return 3
        if(health.withoutStatus) return 2
        if(health.resourcesWarning) return 1
        return 0
      },
      getGroupCriticalHealthCount(health) {
        return health.off + health.withoutStatus + health.resourcesCritical
      },
      getGroupHealthLabel(health) {
        var alerts = []
        if(health.off) alerts.push(health.off + ' apagada' + (health.off == 1 ? '' : 's'))
        if(health.withoutStatus) alerts.push(health.withoutStatus + ' sin estado')
        if(health.resourcesCritical) alerts.push(health.resourcesCritical + ' con recursos críticos')
        if(health.resourcesWarning) alerts.push(health.resourcesWarning + ' con recursos altos')
        if(health.processing) alerts.push(health.processing + ' procesando')
        return alerts.length ? 'Alertas del grupo: ' + alerts.join(', ') : 'Grupo sin alertas'
      },
      isServerProcessing(item) {
        return item.status_install || this.getServerDetails(item, 'status').status == 'Reiniciando'
      },
      getServerResources(item) {
        if(this.showFieldServer(item, 'status') != 'Iniciado') {
          return []
        }
        return [
          { key: 'cpu', label: 'CPU', title: 'Uso de CPU', icon: 'memory' },
          { key: 'memory', label: 'RAM', title: 'Uso de memoria', icon: 'memory' },
          { key: 'disk', label: 'Disco', title: 'Uso de disco', icon: 'storage' },
        ].filter(resource => item[resource.key])
      },
      formatResourceUsage(item, key) {
        return Intl.NumberFormat('es-AR', { style: 'percent', maximumFractionDigits: 0 }).format(this.showFieldServer(item, key))
      },
      getResourceTone(item, key) {
        var usage = this.showFieldServer(item, key)
        if(usage >= 0.9) return 'text-danger'
        if(usage > 0.8) return 'text-warning'
        return 'text-success'
      },
      getServerServices(item) {
        if(this.showFieldServer(item, 'status') != 'Iniciado' || item.status_install || item.type == 'backups') {
          return []
        }
        return [
          { key: 'git', label: 'Git', icon: 'account_tree' },
          { key: 'apache', label: 'Apache', icon: 'language' },
          { key: 'composer', label: 'Composer', icon: 'extension' },
          { key: 'php', label: 'PHP', icon: 'code' },
          { key: 'mysql', label: 'MySQL', icon: 'database' },
          { key: 'supervisor', label: 'Supervisor', icon: 'settings' },
          { key: 'certbot', label: 'Certbot', icon: 'verified_user' },
          { key: 'docker', label: 'Docker', icon: 'deployed_code' },
        ].filter(service => item[service.key])
      },
      getServiceTone(item, key) {
        return this.getServerDetails(item, key).status == 'Activo' ? 'text-success' : 'text-danger'
      },
      // select
      loadInstancesCategories(object) {
        if(object){
          this.crud.form.categories = object
          this.crud.form.categories_id = object.id                 
        } else {
          this.crud.form.categories = null
          this.crud.form.categories_id = 0
        }               
      },

      // filters
      filterLoadCategories() {
        var result = serviceAPI.obtenerCategoriesByType(this.filterType)
        result.then((response) => {
          var data = response.data

          data.forEach(element => {
            this.arr.select.categories.push({code: element.id, label: element.name})
          });
        })
      },           

      // visible password
      changePasswordServer() {
        if (this.password.typePasswordServer === 'password') {
          this.password.typePasswordServer = 'text'
          this.password.iconPasswordServer = 'visibility_off'
        } else {
          this.password.typePasswordServer = 'password'
          this.password.iconPasswordServer  = 'visibility'
        }
      },            
      changePasswordDatabase() {
        if (this.password.typePasswordDatabase === 'password') {
          this.password.typePasswordDatabase = 'text'
          this.password.iconPasswordDatabase = 'visibility_off'
        } else {
          this.password.typePasswordDatabase = 'password'
          this.password.iconPasswordDatabase  = 'visibility'
        }
      },
      
      // fields server table 
      showFieldServer(item, type) {
        var data = null
        var obj = null

        if(type == 'cpu') {
          obj = JSON.parse(item.cpu)

          if(obj && obj.usage) {
            data = parseFloat(obj.usage.replace('%','')) / 100
          }
        }

        if(type == 'status') {
          obj = JSON.parse(item.status)

          if(obj && obj.status) {
            data = obj.status
          }            
        }

        if(type == 'disk') {
          obj = JSON.parse(item.disk)

          if(obj && obj.usage_percent) {
            data = parseFloat(obj.usage_percent.replace('%','')) / 100
          }
        }
        
        if(type == 'memory') {
          obj = JSON.parse(item.memory)   
          
          if(obj && obj.usage_percent) {            
            data = parseFloat(obj.usage_percent.replace('%','')) / 100
          }
        }

        if(type == 'git') {          
          obj = JSON.parse(item.git)

          if(obj && obj.status) {
            data = obj.status
          }
        }

        if(type == 'apache') {          
          obj = JSON.parse(item.apache)

          if(obj && obj.status) {
            data = obj.status
          }
        }
        
        if(type == 'composer') {          
          obj = JSON.parse(item.composer)

          if(obj && obj.status) {
            data = obj.status
          }
        }
        
        if(type == 'php') {          
          obj = JSON.parse(item.php)

          if(obj && obj.status) {
            data = obj.status
          }
        }

        if(type == 'mysql') {
          obj = JSON.parse(item.mysql)

          if(obj && obj.status) {
            data = obj.status
          }
        }

        if(type == 'supervisor') {
          obj = JSON.parse(item.supervisor)

          if(obj && obj.status) {
            data = obj.status
          }
        }
        
        if(type == 'certbot') {          
          obj = JSON.parse(item.certbot)

          if(obj && obj.status) {
            data = obj.status
          }
        }

        if(type == 'docker') {          
          obj = JSON.parse(item.docker)

          if(obj && obj.status) {
            data = obj.status
          }
        }

        return data
      },

      // log
      logServer(item) {
        this.tableLog.items = []
        this.tableLog.currentPage = 1
        this.tableLog.isBusy = true
        this.modal.log.title = "Log Servidor: [" + item.name + "]"
        this.modal.log.active = true

        var result = serviceAPI.filtrarServidorLog({
          instances_server_id: item.id,
        })
        result.then((response) => {
          var data = response.data
          this.tableLog.isBusy = false

          this.tableLog.items = data
        })
        .catch(() => {
          this.tableLog.isBusy = false
          //this.$awn.alert(Error.showError(error))
        });
      },
      closeLogServer() {
        this.modal.log.active=false
      },

      getAccessSSH(item) {        
        var command = document.getElementById("command_terminal");
        var commandSSH = document.createElement("div");
        commandSSH.setAttribute("id", 'command_ssh');
        commandSSH.textContent = 'ssh ' + item.user + '@' + item.host + " -p " + item.port;   
        command.appendChild(commandSSH);
        
        var codigoACopiar = document.getElementById('command_ssh')        
        var seleccion = document.createRange(); 
        seleccion.selectNodeContents(codigoACopiar);

        window.getSelection().removeAllRanges();
        window.getSelection().addRange(seleccion); 

        try {
          var res = document.execCommand('copy');
          if (res){
            this.$awn.success("Accesos via SSH copiado al portapapeles");
            setTimeout(()=> {
              this.$awn.info("Contraseña: " + item.password);
            },1500)            
          } else {
            this.$awn.alert("Error al copiar acceso");
          }    
          
          commandSSH.remove()
        }
        catch(ex) {
          commandSSH.remove()            
        }
        window.getSelection().removeRange(seleccion);        
      },

      // action
      actionServer(item) {
        this.selectedServer = item
        this.SSHConsole = ''

        this.modal.command._id = Math.random().toString(36).slice(2, 8)
        this.modal.command.title = "Comandos Servidor: [" + item.name + "]"
        this.modal.command.subtitle = "Terminal"

        this.modal.command.active = true
      },
      closeActionServer() {
        this.modal.command.active=false
        this.filter()
      },
     copySSHConsole() {
        var consoleElement = this.$refs.sshConsole
        var output = consoleElement ? (consoleElement.innerText || consoleElement.textContent || '') : ''
        if (!output) {
          this.$awn.alert('No hay salida para copiar')
          return
        }

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(output)
            .then(() => this.$awn.success('Salida copiada al portapapeles'))
            .catch(() => this.copySSHConsoleFallback(output))
          return
        }

        this.copySSHConsoleFallback(output)
      },
      copySSHConsoleFallback(output) {

        var textarea = document.createElement('textarea')
        textarea.value = output
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        textarea.setSelectionRange(0, textarea.value.length)

        var copied = document.execCommand('copy')
        textarea.remove()
        copied ? this.$awn.success('Salida copiada al portapapeles') : this.$awn.alert('No se pudo copiar la salida')
      },
      refreshServer() {
        var result = serviceAPI.mostrarServidor(this.selectedServer.id);
        
        result.then((response) => {
          var data = response.data

          this.selectedServer = data

          this.$awn.success("Servidor refrescado");
        }) 
      },
 
      getActionServer(reference, titulo) {

        // titulo de referencia
        this.modal.command.subtitle = titulo

        // si reinicia el servidor, lo maneja otro evento
          if(reference == 'server-restart') {
            this.restartServer(reference)
            return false
          }
        // fin

        // si instala servicios en el servidor, lo maneja otro evento
          if(reference == 'server-install') {
            this.installServer(reference, 'instalar todos los SERVICIOS', 'Instalar SERVIDOR')
            return false
          }
          if(reference == 'git-install') {
            this.installServer(reference, 'instalar el servicio GIT', 'Instalar GIT')
            return false
          }
          if(reference == 'apache-install') {
            this.installServer(reference, 'instalar el servicio APACHE', 'Instalar APACHE')
            return false          
          }
          if(reference == 'composer-install') {
            this.installServer(reference, 'instalar el servicio COMPOSER', 'Instalar COMPOSER')
            return false
          }
          if(reference == 'php-install') {
            this.installServer(reference, 'instalar el servicio PHP', 'Instalar PHP')
            return false
          }
          if(reference == 'mysql-install') {
            this.installServer(reference, 'instalar el servicio MYSQL', 'Instalar MYSQL')
            return false
          }
          if(reference == 'supervisor-install') {
            this.installServer(reference, 'instalar el servicio SUPERVISOR', 'Instalar SUPERVISOR')
            return false
          }
          if(reference == 'certbot-install') {
            this.installServer(reference, 'instalar el servicio CERTBOT', 'Instalar CERTBOT')
            return false
          }
          if(reference == 'docker-install') {
            this.installServer(reference, 'instalar el servicio DOCKER', 'Instalar DOCKER')
            return false
          }          
        // fin
          
        // comandos
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } else {
          item = this.selectedServer
        }

        this.SSHLoader = true
        var result = serviceAPI.commandActionServidor({
          id: item.id, 
          command: reference
        });

        result.then((response) => {                    
          var data = response.data

          this.SSHConsole = data
          this.filter()
          this.SSHLoader = false
        })
        .catch(error => {          
          this.SSHConsole = 'Inactivo'
          this.SSHLoader = false
          this.$awn.alert(Error.showError(error));
        })
      },
      restartServer(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } else {
          item = this.selectedServer
        }
                
        this.confirmDialog('¿Desea reiniciar servidor (' + item.name + ')?', {
          title: 'Reiniciar Servidor',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionServidor({
              id: item.id, 
              command: reference
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Inactivo'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })        
      },    
      installServer(reference, titleLegend, titleModal) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } else {
          item = this.selectedServer
        }
                
        this.confirmDialog('¿Desea ' + titleLegend + ' en VPS: ' + item.name.toUpperCase() + '?', {
          title: titleModal,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionServidor({
              id: item.id, 
              command: reference
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en la instalación'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })        
      },  
      
      // FILTROS SAVE
      saveFilterStorage() {
        Storage.setValue('filter_view_instances_server', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_view_instances_server')) {
          this.filters = JSON.parse(Storage.getValue('filter_view_instances_server'))
        }
        if(!this.filters.health) {
          this.filters.health = { code: 'all', label: 'Todos' }
        }
      },       
    }    
  }
</script>
<style>
  .ssh-console-server {
    background-color: black;
    background-image: radial-gradient(
      rgba(0, 150, 0, 0.75), black 120%
    );
    height: 410px;
    color: white;
    font: 13px Inconsolata, monospace;    
    text-shadow: 0 0 5px #C8C8C8;
    padding: 10px;
    white-space: pre;
    overflow: auto;
  }
  .ssh-console-server::after {
    content: "";
    /*position: absolute;*/
    top: 0;
    left: 0;
    width: 100vw;
    height: 410px;
    background: repeating-linear-gradient(
      0deg,
      rgba(black, 0.15),
      rgba(black, 0.15) 1px,
      transparent 1px,
      transparent 2px
    );
    padding: 10px;
  }  
  .ssh-console-server::selection {
    background: #0080FF;
    text-shadow: none;
  }  
  .ssh-console-footer {
    z-index: 9999;
  }
  .app-command-terminal-column { display: flex; }
  .app-command-terminal { display: flex; flex: 1; flex-direction: column; min-width: 0; }
  .app-command-terminal__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 36px; margin-bottom: 8px; }
  .app-command-terminal__header h4 { margin: 0; color: var(--app-color-text); font-size: 20px; font-weight: 600; line-height: 1.2; }
  .app-command-terminal__actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; min-height: 28px; }
  .app-command-terminal__process { display: inline-flex; align-items: center; gap: 4px; color: var(--app-color-primary); font-size: 12px; font-weight: 600; }
  .btn.app-command-terminal__copy { display: inline-flex; align-items: center; height: 28px; padding: 0 8px; border-radius: 4px; font-size: 12px; }
  .btn.app-command-terminal__refresh { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 28px; padding: 0; border-radius: 4px; }
  .app-command-terminal__loader { display: flex; flex: 1; align-items: center; justify-content: center; min-height: 175px; background: #071009; }
  .ssh-console-server { flex: 0 0 420px; box-sizing: border-box; height: 420px !important; max-height: calc(100vh - 220px); padding: 10px; overflow-x: auto; overflow-y: auto !important; border-radius: 0; background-color: #071009; background-image: radial-gradient(ellipse at center, #173b1a 0%, #0b1d0e 52%, #071009 100%); color: #fff; font: 13px Inconsolata, monospace; line-height: 1.45; overflow-wrap: anywhere; text-shadow: none; }
  .ssh-console-server::after { display: none; }
  @media (max-width: 767.98px) { .app-command-terminal__header { align-items: flex-start; }.app-command-terminal__header h4 { font-size: 18px; }.ssh-console-server { flex-basis: 320px; height: 320px !important; max-height: calc(100vh - 190px); } }
  .instance-server-divide {
    margin-top: 1px;
    margin-bottom: 10px;    
  }
  .instances-server-log-loader,
  .instances-server-list-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 180px;
    color: var(--app-color-text-secondary);
    font-size: 13px;
  }
  .instances-server-list-loader {
    min-height: 240px;
  }
  .instances-server-command-panel-column {
    display: flex;
  }
  .instances-server-command-panel {
    width: 100%;
    max-height: 450px;
    padding: 10px;
    overflow-y: auto;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
  }
  .instances-server-command-panel h6 {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 2px 4px 9px;
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
  }
  .instances-server-command-group {
    border-top: 1px solid var(--app-color-border);
  }
  .instances-server-command-group:last-child {
    border-bottom: 1px solid var(--app-color-border);
  }
  .btn.instances-server-command-group__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 36px;
    padding: 7px 4px;
    color: var(--app-color-text);
    font-size: 13px;
    font-weight: 700;
    text-align: left;
  }
  .instances-server-command-group__toggle > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .instances-server-command-group__toggle:hover,
  .instances-server-command-group__toggle:focus {
    color: var(--app-color-primary);
    text-decoration: none;
  }
  .instances-server-command-group__actions {
    display: grid;
    gap: 2px;
    padding: 0 0 8px;
  }
  .instances-server-command-subsection {
    display: block;
    margin: 8px 0 3px;
    padding: 5px 7px;
    border-top: 1px solid var(--app-color-border);
    border-left: 3px solid var(--app-color-primary);
    background: var(--app-color-surface-muted);
    color: var(--app-color-text-secondary);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
  }
  .instances-server-command-subsection:first-child {
    margin-top: 1px;
    border-top: 0;
  }
  .btn.instances-server-command-action {
    display: flex;
    align-items: center;
    gap: 7px;
    width: 100%;
    min-height: 30px;
    padding: 5px 7px;
    border-radius: 5px;
    color: var(--app-color-text-secondary);
    font-size: 12px;
    text-align: left;
  }
  .btn.instances-server-command-action:hover,
  .btn.instances-server-command-action:focus {
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    text-decoration: none;
  }
  .btn.instances-server-command-action--danger:hover,
  .btn.instances-server-command-action--danger:focus {
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger-hover);
  }
  .instances-server-groups {
    display: grid;
    gap: 16px;
  }
  .instances-server-view-switcher {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    margin-right: 7px;
    padding-right: 7px;
    border-right: 1px solid var(--app-color-border);
  }
  .instances-server-view-switcher .btn.list-view-icon-btn.list-view-icon-btn--active {
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
  }
  .instances-server-group-card {
    border-color: var(--app-color-border);
    border-radius: 10px;
    box-shadow: var(--app-shadow-sm);
    overflow: hidden;
  }
  .instances-server-group-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border-bottom: 1px solid var(--app-color-border);
    background: var(--app-color-surface-subtle);
    border-top: 0;
    border-right: 0;
    border-left: 0;
    color: inherit;
    text-align: left;
  }
  .instances-server-group-card__header:hover { background: var(--app-color-primary-soft); }
  .instances-server-group-card__header--static { cursor: default; }
  .instances-server-group-card__header--static:hover { background: var(--app-color-surface-subtle); }
  .instances-server-group-card__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
  }
  .instances-server-group-card__header h3,
  .instances-server-card__title h4 {
    margin: 0;
    color: var(--app-color-text);
  }
  .instances-server-group-card__header h3 {
    font-size: 14px;
    line-height: 1.15;
  }
  .instances-server-group-card__summary {
    min-width: 0;
  }
  .instances-server-group-card__summary > span {
    color: var(--app-color-text-secondary);
    font-size: 11px;
  }
  .instances-server-group-card__health {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
    margin-left: auto;
  }
  .instances-server-group-card__health-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 7px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    white-space: nowrap;
  }
  .instances-server-group-card__health-item .material-symbols-outlined {
    font-size: 15px;
  }
  .instances-server-group-card__health-item--success {
    border-color: var(--app-color-success-border);
    background: var(--app-color-success-soft);
    color: var(--app-color-success-hover);
  }
  .instances-server-group-card__health-item--warning {
    border-color: var(--app-color-warning-border);
    background: var(--app-color-warning-soft);
    color: var(--app-color-warning-hover);
  }
  .instances-server-group-card__health-item--danger {
    border-color: var(--app-color-danger-border);
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger-hover);
  }
  .instances-server-group-card__health-item--info {
    border-color: var(--app-color-primary-200);
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
  }
  .instances-server-group-card__toggle {
    margin-left: auto;
    color: var(--app-color-text-secondary);
  }
  .instances-server-group-card__body {
    padding: 10px;
    background: var(--app-color-canvas);
  }
  .instances-server-group-card__cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 10px;
  }
  .instances-server-card {
    padding: 12px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface);
    box-shadow: var(--app-shadow-sm);
  }
  .instances-server-card__heading {
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .instances-server-card__title,
  .instances-server-card__meta,
  .instances-server-card__alert {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .instances-server-card__title h4 {
    font-size: 17px;
  }
  .instances-server-card__id {
    color: var(--app-color-text-secondary);
    font-size: 12px;
    font-weight: 600;
  }
  .instances-server-card__meta {
    flex-wrap: wrap;
    margin-top: 6px;
    font-size: 12px;
  }
  .instances-server-card__alerts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 10px;
    margin-top: 14px;
  }
  .instances-server-card__alert {
    align-items: flex-start;
    padding: 10px 12px;
    border: 1px solid var(--app-color-border);
    border-left-width: 4px;
    border-radius: 4px;
  }
  .instances-server-card__alert > .material-symbols-outlined {
    margin-top: 1px;
    font-size: 22px;
  }
  .instances-server-card__alert div {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .instances-server-card__alert b {
    font-size: 13px;
  }
  .instances-server-card__alert span {
    color: var(--app-color-text-secondary);
    font-size: 12px;
    line-height: 1.35;
  }
  .instances-server-card__alert--notification {
    border-left-color: var(--app-color-warning);
  }
  .instances-server-card__alert--notification > .material-symbols-outlined {
    color: var(--app-color-warning);
  }
  .instances-server-card__alert--observation {
    border-left-color: var(--app-color-primary);
  }
  .instances-server-card__alert--observation > .material-symbols-outlined {
    color: var(--app-color-primary);
  }
  .instances-server-card__metrics {
    display: grid;
    grid-template-columns: minmax(90px, .75fr) minmax(150px, 1.25fr);
    gap: 14px;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid var(--app-color-border);
  }
  .instances-server-card__section {
    display: grid;
    align-content: start;
    justify-items: center;
    gap: 8px;
  }
  .instances-server-card__section--services {
    grid-column: 1 / -1;
  }
  .instances-server-card__section-label {
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .03em;
    text-transform: uppercase;
  }
  .instances-server-name__title,
  .instances-server-name__meta,
  .instances-server-resources,
  .instances-server-services {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .instances-server-name__title {
    min-width: 0;
  }
  .instances-server-tree__category {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }
  .instances-server-tree__node {
    border-left: 1px solid var(--app-color-border);
    margin: 3px 0 0 7px;
    padding-left: 9px;
  }
  .instances-server-name__meta {
    flex-wrap: wrap;
    margin-top: 4px;
  }
  .instances-server-name__link {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-top: 4px;
    font-size: 12px;
  }
  .instances-server-alert {
    display: inline-flex;
    align-items: center;
  }
  .instances-server-resources {
    justify-content: center;
  }
  .instances-server-resource {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 54px;
    cursor: pointer;
    font-size: 13px;
    line-height: 1.1;
  }
  .instances-server-resource small {
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 600;
  }
  .instances-server-services {
    justify-content: center;
    flex-wrap: wrap;
    max-width: 280px;
  }
  .instances-server-service {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 62px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.1;
  }
  .instances-server-service small {
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 400;
  }
  .instances-server-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 9px;
    cursor: pointer;
    font-size: 12px;
  }
  .instances-server-tree-view {
    display: grid;
    gap: 2px;
  }
  .instances-server-tree-view__server {
    position: relative;
    display: grid;
    grid-template-columns: 32px minmax(190px, .8fr) minmax(0, 2.2fr);
    align-items: center;
    min-height: 46px;
    border-bottom: 1px solid var(--app-color-border);
    background: var(--app-color-surface);
  }
  .instances-server-tree-view__server:last-child { border-bottom: 0; }
  .instances-server-tree-view__server-header {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 7px 8px 7px 0;
  }
  .instances-server-tree-view__branch {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    color: var(--app-color-primary);
  }
  .instances-server-tree-view__identity {
    flex: 1;
    min-width: 0;
  }
  .instances-server-tree-view__identity .instances-server-card__title h4 {
    font-size: 15px;
  }
  .instances-server-tree-view__status {
    flex-shrink: 0;
  }
  .instances-server-tree-view__details {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 7px 12px;
  }
  .instances-server-tree-view__item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    font-weight: 600;
    font-size: 12px;
  }
  .instances-server-tree-view__item .material-symbols-outlined { font-size: 17px; }
  .instances-server-tree-view__item b { color: var(--app-color-text-secondary); font-weight: 600; }
  .instances-server-tree-view__item.text-warning,
  .instances-server-tree-view__item.text-danger {
    padding: 3px 6px;
    border: 1px solid;
    border-radius: 4px;
  }
  .instances-server-tree-view__item.text-warning {
    border-color: var(--app-color-warning-border);
    background: var(--app-color-warning-soft);
    color: var(--app-color-warning-hover);
  }
  .instances-server-tree-view__item.text-danger {
    border-color: var(--app-color-danger-border);
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger-hover);
  }
  .instances-server-tree-view__item.text-warning b,
  .instances-server-tree-view__item.text-danger b { color: inherit; }
  .instances-server-tree-view__link,
  .instances-server-tree-view__more { display:inline-flex; align-items:center; gap:3px; font-size:11px; white-space:nowrap; }
  .instances-server-tree-view__identity .instances-server-card__title {
    min-width: 0;
    flex-wrap: wrap;
  }
  .instances-server-tree-view__identity .instances-server-card__title h4 {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .instances-server-tree-view__link {
    max-width: 100%;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .instances-server-tree-view__server-header > .btn-group,
  .instances-server-tree-view__server-header > .dropdown {
    flex: 0 0 auto;
  }
  .instances-server-tree-view__more { cursor:pointer; color:var(--app-color-primary); }
  .instances-server-tree-view__more .material-symbols-outlined { font-size:18px; }
  .instances-server-tree-view__empty {
    color: var(--app-color-text-secondary);
    font-size: 12px;
    font-style: italic;
  }
  @media (max-width: 767.98px) {
    .instances-server-command-panel-column {
      display: block;
      margin-bottom: 14px;
    }
    .instances-server-command-panel {
      max-height: none;
    }
    .instances-server-group-card__header {
      align-items: flex-start;
      flex-wrap: wrap;
    }
    .instances-server-group-card__health {
      order: 3;
      flex-basis: calc(100% - 34px);
      justify-content: flex-start;
      margin-left: 34px;
    }
    .instances-server-group-card__toggle {
      margin-left: auto;
    }
    .instances-server-group-card__body {
      padding: 8px;
    }
    .instances-server-group-card__cards {
      grid-template-columns: 1fr;
    }
    .instances-server-card {
      padding: 13px;
    }
    .instances-server-card__metrics {
      grid-template-columns: 1fr;
      gap: 16px;
    }
    .instances-server-card__section--services {
      grid-column: auto;
    }
    .instances-server-card__section {
      justify-items: start;
    }
    .instances-server-resources,
    .instances-server-services {
      justify-content: flex-start;
      max-width: none;
    }
    .instances-server-tree-view__server {
      grid-template-columns: 27px minmax(0, 1fr);
    }
    .instances-server-tree-view__details {
      grid-column: 2;
      padding: 0 8px 9px 0;
      gap: 8px;
    }
    .instances-server-tree-view__server-header {
      flex-wrap: wrap;
    }
    .instances-server-tree-view__status {
      margin-left: 0;
    }
  }
  .btn.crud-instances-server-action-toggle-labeled {
    display: inline-flex !important;
    align-items: center;
    justify-content: flex-start;
    width: auto !important;
    height: auto !important;
    min-height: 0;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    font-size: 13px;
    line-height: 1.15;
  }
  .btn.crud-instances-server-action-toggle-labeled:hover,
  .btn.crud-instances-server-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  @media (max-width: 767.98px) {
    .app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
      content: attr(data-label) !important;
    }
    .app-table.b-table-stacked-md > tbody > tr > td:last-child {
      text-align: left !important;
    }
  }
</style>
